// WS5 PR5 — Ride-mode + pin (ride between AABB headboxes, hides anisotropic
// GS quality drop). Layer puro src/ride/*: types/state-machine/motion/
// edge-trigger/pin-sprite. Orchestrator wiring qui sotto.
import * as THREE_PR5 from 'three';
import type { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { ApiClient } from '../api/client.js';
import { ApiError, isAbortError, NetworkError } from '../api/error.js';
// WS5 PR3.2a/PR3.2b: multi-viewbox state + AABB clamp + headbox decrypt
// helper (scope shareToken in PR4 mobile/share; oggi normalizeManifestViewboxes
// pass-through per scene pubbliche + fail-loud su payload offset senza token).
import { normalizeManifestViewboxes } from '../api/headbox-privacy.js';
import type { FloorPlan, Hotspot, ModelMeta, ModelMetaVariant, Preset, Vec3 } from '../api/types.js';
import {
  cameraHeadingDeg,
  isCalibrationDegenerate,
  pixelToWorld,
  worldToPixel,
} from '../floorplan/calibration.js';
import { floorplanSnapshot, floorplanTransition } from '../floorplan/state-machine.js';
import type {
  FloorplanEvent,
  FloorplanPosition,
  FloorplanSnapshot,
  FloorplanState,
} from '../floorplan/types.js';
import { fadeOpacity } from '../hotspots/fade.js';
import { hotspotsSnapshot, hotspotsTransition, sortHotspots } from '../hotspots/state-machine.js';
import type {
  HotspotScreenPosition,
  HotspotsEvent,
  HotspotsSnapshot,
  HotspotsState,
} from '../hotspots/types.js';
import { t as i18nT } from '../i18n/init.js';
import type { DeviceCapabilities, DeviceMatcher } from '../lib/device.js';
import { detectDeviceClass, readDeviceClassInputs } from '../lib/device.js';
import { createFpsMonitor } from '../lib/fps-monitor.js';
import { exitFullscreen, isFullscreen, requestFullscreen } from '../lib/fullscreen.js';
import { bindShortcuts } from '../lib/keyboard.js';
import { defaultScreenshotFilename, downloadBlob } from '../lib/screenshot.js';
import { SHORTCUTS } from '../lib/shortcuts.js';
import { type BlobUrlHandle, loadModelAsBlobUrl } from '../loader/model-loader.js';
import { selectVariantForDevice } from '../loader/variant-resolver.js';
import { measurementsSnapshot, measurementsTransition } from '../measure/state-machine.js';
import type {
  MeasurementProjection,
  MeasurementsEvent,
  MeasurementsSnapshot,
  MeasurementsState,
  MeasurementUnits,
} from '../measure/types.js';
import {
  type EdgeTrackingState,
  electCandidateViewbox,
  initEdgeTracking,
  isEdgeOutwardFrame,
  trackEdgePersistence,
} from '../ride/edge-trigger.js';
import {
  computeRideDuration,
  computeRideTransform,
  distance3,
  lookAtYawPitch,
} from '../ride/motion.js';
import {
  buildMobileVisibleSet,
  computePinVisibility,
  createPinSprite,
  disposeSharedTextures as disposeRidePinTextures,
  loadPinTexture,
  type PinSpritePair,
  type PinSpriteUserData,
} from '../ride/pin-sprite.js';
import { isInCooldown, isRideDone, rideSnapshot, rideTransition } from '../ride/state-machine.js';
import {
  INITIAL_RIDE_STATE,
  type RideEvent,
  type RideSnapshot,
  type RideState,
  type RideTrigger,
} from '../ride/types.js';
import { copyText } from '../share/clipboard.js';
import { decodeCameraState, encodeCameraState } from '../share/codec.js';
import { buildShareUrl } from '../share/url-builder.js';
import type { AppStore } from '../state/store.js';
import type { AppState, ParsedRoute, SceneMetadata } from '../state/types.js';
import { type CameraTarget, flyToCamera } from '../tour/animator.js';
import { sortPresets, tourSnapshot, tourTransition } from '../tour/state-machine.js';
import { type TourEvent, type TourSnapshot, type TourState, TRANSITION_MS } from '../tour/types.js';
import { parseShareUrl } from '../url/parser.js';
import { clampEyeToHeadbox } from '../viewbox/aabb.js';
import { runCinematicTour } from '../viewbox/cinematic-tour.js';
import { viewboxSnapshot, viewboxTransition } from '../viewbox/state-machine.js';
import {
  INITIAL_VIEWBOX_STATE,
  TELEPORT_GLIDE_MS,
  TELEPORT_GLIDE_MS_MOBILE,
  type ViewboxEvent,
  type ViewboxSnapshot,
  type ViewboxState,
} from '../viewbox/types.js';
import {
  canCaptureFrame,
  canComputeOverviewCamera,
  canDispose,
  canEnterXR,
  canExitXR,
  canFitCamera,
  canRaycast,
  canSetBeforeRender,
  canSetPostFx,
  canSubscribeFrames,
  canUseEngineNeutralCamera,
  type ViewerAdapter,
} from '../viewer/adapter.js';
import {
  projectWorldToScreenDispatch,
  raycastDispatch,
  resizeDispatch,
  setCameraDispatch,
} from '../viewer/camera-bridge.js';
import { createOrbitControls } from '../viewer/orbit-controls.js';
import {
  DEFAULT_POST_FX_OPTIONS,
  type PostFxSnapshot,
  readPostFxPreference,
  resolvePostFxSnapshot,
  writePostFxPreference,
} from '../viewer/post-fx.js';
import type { InitOptions } from '../viewer/types.js';
import { computeMoveTarget, DEFAULT_CLICK_TO_MOVE, moveDurationMs } from '../walk/click-to-move.js';
import { createJoystickController, type JoystickController } from '../walk/joystick.js';
import { isMobileFpEnabled } from '../walk/mobile-fp-flag.js';
import {
  applyGravity,
  approachEyeHeight,
  clampPitch,
  computeVelocity,
  forwardVector,
  normalizeWalkKey,
  selectWalkSpeed,
} from '../walk/movement.js';
import { walkSnapshot, walkTransition } from '../walk/state-machine.js';
import { createTouchControls, type TouchControls } from '../walk/touch-controls.js';
import {
  CROUCH_EYE_HEIGHT,
  EYE_HEIGHT,
  EYE_HEIGHT_TRANSITION_SPEED,
  FLOOR_HEIGHT,
  JUMP_VELOCITY,
  MOUSE_SENSITIVITY,
  ON_FLOOR_EPSILON,
  type WalkEvent,
  type WalkSnapshot,
  type WalkState,
  type WalkTouchInput,
} from '../walk/types.js';
import { detectXRSupport } from '../xr/detection.js';
import { xrSnapshot, xrTransition } from '../xr/state-machine.js';
import {
  INITIAL_XR_STATE,
  type XREvent,
  type XRMode,
  type XRSnapshot,
  type XRState,
} from '../xr/types.js';
import { fetchMetadataTolerant } from './parallel-metadata.js';

export interface OrchestratorDeps {
  apiClient: ApiClient;
  adapter: ViewerAdapter;
  store: AppStore;
  canvas: HTMLCanvasElement;
  href: string;
  embed: boolean; // controls fallback init opts
  // 4.M: pointer / hover / reduced-motion live capability matcher.
  // Owned by bootstrap; orchestrator subscribes + re-broadcasts via
  // 'device-state' event so the renderer stays event-driven (pattern
  // affermato `orchestrator emits → render subscribes`).
  deviceMatcher: DeviceMatcher;
  // Step 7 Phase 0 — optional async hook called AFTER fetchMeta success
  // and BEFORE dispatch META_FETCHED. Returning false dispatches
  // LOAD_CANCELLED_BY_USER instead. Used by main.ts bootstrap to show
  // mobile OOM guardrail modal when phone+ply+size>150MB. The hook
  // receives signal so it can react to dispose/abort while modal is
  // open (e.g. user navigates away).
  shouldProceedAfterMeta?: (meta: ModelMeta, ctx: { signal: AbortSignal }) => Promise<boolean>;
}

// 4.M walk-error reason discriminator. The toast component picks
// one of 4 contextual messages based on (reason, embed) tuple.
export type WalkErrorReason = 'pointer-lock-failed' | 'touch' | 'unsupported';

export interface OrchestratorEventDetailMap {
  'perf-toggle': { visible: boolean };
  'fps-update': { fps: number };
  'fps-warning': { fps: number };
  'fullscreen-error': { message: string };
  'share-result': { success: boolean; message?: string };
  // 4.F: tour state-machine snapshot. Emitted on every real state transition
  // and throttled to ~10 hz on TICK no-op for dwell countdown UI.
  'tour-state': TourSnapshot;
  // 4.F: emitted only when applyPreset runs the manual idle path; tour-driven
  // preset changes are reflected via 'tour-state' events instead.
  'preset-applied': { presetId: string };
  // 4.G: hotspot UI state snapshot — emitted on real state transitions
  // (open/close/next/prev/toggle visibility).
  'hotspot-state': HotspotsSnapshot;
  // 4.G: per-frame projected screen positions for each hotspot. Emitted
  // only when loaded + visible + total > 0 (see attachLoadedFeatures
  // ticker); UI components apply transform: translate(x, y) without
  // touching Three.js.
  'hotspot-positions': { positions: readonly HotspotScreenPosition[] };
  // 4.H: floor plan minimap state snapshot.
  'floorplan-state': FloorplanSnapshot;
  // 4.H: per-frame projected camera position (pixel space) + heading.
  // Emitted only when floorplan is present, visible, and calibration is
  // not degenerate (cleanup contract).
  'floorplan-position': FloorplanPosition;
  // 5.D: floor plan replaced (cardinality 0<->1 OR new image_url).
  // Renderer cleanup+remount minimap on this event so the dot/cone
  // start fresh and don't show stale calibration for one frame.
  'floorplan-replaced': { hadFloorplan: boolean; hasFloorplan: boolean };
  // 4.J: measurements state snapshot (active toggle + points + units).
  'measurement-state': MeasurementsSnapshot;
  // 4.J: per-frame projected screen positions for each measurement
  // point + cumulative segment distances (meters). Emitted only when
  // measureState.active && points.length > 0 (cleanup contract).
  'measurement-projections': {
    projections: readonly MeasurementProjection[];
    distances: readonly number[];
  };
  // 4.I1: walk mode (FPV) state snapshot. Emitted on toggle + on
  // automatic exits (pointer-lock unsupported / browser ESC / etc.).
  'walk-state': WalkSnapshot;
  // 4.K: WebXR (VR / AR) modal + session state snapshot. Emitted on
  // every real state transition (open/close modal, detection result,
  // session start/end, error). dispatchXREvent guards stale snapshot
  // emission after nested rollback (same pattern as walk-state).
  'xr-state': XRSnapshot;
  // 4.M: device capability snapshot (pointer modality, hover, reduced
  // motion). Emitted on every matchMedia change so toolbar / hints /
  // walk gating reactively update without remounting Loaded.
  'device-state': DeviceCapabilities;
  // Inc D: post-FX state snapshot (available / userPreference / effective).
  // Emitted on toggle, tour start/exit, device-state change, and initial seed.
  'post-fx-state': PostFxSnapshot;
  // 4.M: walk failure context (pointer-lock unsupported / iframe
  // policy denial / touch device). Emitted alongside the existing
  // 'walk-state' EXIT so the UI can show a toast with explanation.
  'walk-error': { reason: WalkErrorReason; embed: boolean };
  // 5.C: emitted after the orchestrator has finished wiring controls
  // and loaded features (post attachOrbitControls + attachLoadedFeatures).
  // Studio Web embedded bridge resolves its `ready` Promise on this
  // event — NOT on the store's `kind === 'loaded'` (which fires before
  // controls/features are available, racing capture-camera/raycast).
  'viewer-ready': Record<string, never>;
  // 5.C: emitted on a fatal viewer boot failure (load error after
  // exhaust, adapter init exception). Studio embedded bridge maps to
  // VIEWER_FAILURE editor SM event.
  'viewer-failure': { message: string };
  // 5.C iter 6 fix: emitted by replaceHotspots/replacePresets when the
  // sorted list cardinality crosses 0↔N. Triggers a renderer remount of
  // the Loaded component so newly-created hotspot/preset overlays appear
  // without a page refresh (mountHotspotDots etc. are no-op when wired
  // with an empty list).
  'metadata-replaced': { kind: 'hotspots' | 'presets' };
  // WS5 PR3.2a: viewbox state snapshot. Emesso a LOAD manifest, SELECT,
  // TELEPORT_START/COMPLETE/ABORT (PR3.2b). UI switcher / walk-hud pill /
  // first-run coach (PR3.3) consumano questo evento per render reactive.
  'viewbox-state': ViewboxSnapshot;
  // WS5 PR3.3c: cinematic auto-tour state. Emesso a start (active=true) e a
  // fine (active=false, qualunque sia il motivo: completion, abort, errore).
  // Toolbar consumer toggle aria-pressed + label/skip; render derive
  // helpAvailable + cinematicActive props per Loaded.
  'cinematic-state': { active: boolean };
  // WS5 PR5: ride-mode snapshot. Emesso a START_TO_VIEWBOX/HOTSPOT, COMPLETE,
  // ABORT. UI (HUD telemetry) puo' mostrare progress bar / overlay durante
  // ride. Walk-HUD pill disabled durante phase != 'idle'.
  'ride-state': RideSnapshot;
}

export interface Orchestrator {
  readonly events: EventTarget;
  start(): Promise<void>;
  dispose(): Promise<void>;
  takeScreenshot(): Promise<void>;
  resetView(): void;
  toggleFullscreen(): Promise<void>;
  togglePerfHud(): void;
  shareUrl(): Promise<void>;
  // 4.F: presets + tour. sortedPresets() returns the canonical session order
  // used by the SM and the pill UI; getTourSnapshot() seeds the renderer's
  // initial Loaded state without waiting for the first event.
  applyPreset(presetId: string): Promise<void>;
  // Inc B: fly camera a una vista d'insieme "dollhouse" dell'intero splat.
  showOverview(): Promise<void>;
  // Inc D: toggle post-FX/cinematic (user preference). No-op on coarse pointer
  // (mobile MVP). The effective render state also factors in the tour.
  togglePostFx(): void;
  getPostFxSnapshot(): PostFxSnapshot;
  startTour(): void;
  pauseTour(): void;
  resumeTour(): void;
  exitTour(): void;
  nextTourStep(): void;
  prevTourStep(): void;
  getSortedPresets(): readonly Preset[];
  getTourSnapshot(): TourSnapshot;
  // 4.G: hotspots presence + interaction. Same provider pattern as 4.F so
  // the renderer can seed Loaded mount without waiting for the first event.
  openHotspot(id: string): void;
  closeHotspot(): void;
  nextHotspot(): void;
  prevHotspot(): void;
  toggleHotspots(): void;
  getSortedHotspots(): readonly Hotspot[];
  getHotspotsSnapshot(): HotspotsSnapshot;
  // 4.H: floor plan minimap controls + getters.
  toggleFloorplan(): void;
  closeFloorplan(): void;
  reportFloorplanImageError(): void;
  // teleportOnFloorplan accepts world XZ. teleportOnFloorplanPixel is a
  // convenience that runs pixelToWorld(cal) + teleport — UI components
  // emit svg pixel coords and wire to this directly without importing
  // the calibration helper.
  teleportOnFloorplan(worldXZ: readonly [number, number]): void;
  teleportOnFloorplanPixel(pixelXY: readonly [number, number]): void;
  getFloorplan(): FloorPlan | null;
  getFloorplanSnapshot(): FloorplanSnapshot;
  // 5.D: author replaces (or removes) the floor plan at runtime. Updates
  // the cached FloorPlan + recomputes degenerate calibration flag, emits
  // 'floorplan-state' (existing) + 'floorplan-replaced' (new). Caller
  // is responsible for ordering — typically called after the studio
  // author flow completes the upload + the GET refetch returns.
  replaceFloorplan(fp: FloorPlan | null): void;
  // 4.J: measurement controls + getters. addMeasurePoint runs raycast
  // internally (early-return on no-hit / degenerate state).
  toggleMeasure(): void;
  // Inc A: click-to-move inside-view toggle (Matterport-style). Mutex con
  // walk/measure/tour; quando attivo un click su un punto-pavimento fa
  // scivolare la camera lì.
  toggleClickToMove(): void;
  getClickToMoveActive(): boolean;
  exitMeasure(): void;
  addMeasurePoint(screenX: number, screenY: number): void;
  removeLastMeasurePoint(): void;
  resetMeasurements(): void;
  setMeasurementUnits(units: MeasurementUnits): void;
  getMeasurementsSnapshot(): MeasurementsSnapshot;
  // True when the adapter exposes a working raycast() — UI panel uses
  // this to render a "Raycasting non disponibile" warning when false.
  getRaycastAvailable(): boolean;
  // 4.I1: walk mode (FPV) controls + getter. Pointer-lock unsupported
  // / denied → entry rolls back automatically (see runWalkEffects).
  toggleWalk(): void;
  exitWalk(): void;
  getWalkSnapshot(): WalkSnapshot;
  // WS5 PR4a (Codex iter3 add#1) — input mode INSTALLATO al toggle walk ON.
  // 'touch' = joystick + drag-rotate + pinch. 'keyboard' = WASD + mouselook
  // + pointer-lock. null = walk inattivo. Stabile per la durata della
  // sessione: Surface 2-in-1 hot switch NON cambia walkInputMode senza un
  // exit+re-enter. Letto dal walk-hud per scegliere overlay coerente.
  getWalkInputMode(): 'keyboard' | 'touch' | null;
  // WS5 PR3.2a: viewbox state snapshot getter. UI switcher, walk-hud pill
  // e first-run coach (PR3.3) chiamano questo per il render iniziale prima
  // del primo evento 'viewbox-state'.
  getViewboxSnapshot(): ViewboxSnapshot;
  // WS5 PR3.2b: teleport tra viewbox via flyToCamera glide. arrayIdx e' la
  // posizione nell'array viewboxState.available (NON il viewbox.index numerico).
  // No-op se idx invalido / stesso corrente / mid-glide / scene legacy.
  // Returns Promise che si risolve al completamento del glide o all'abort.
  selectViewbox(arrayIdx: number): Promise<void>;
  // WS5 PR3.3c: cinematic auto-tour (cfr. PLAN §11). Idle → start tour
  // sequencer su prime min(viewboxes.length, 5) viewbox + landing su
  // default_viewbox_index. Active → abort + land veloce. No-op se
  // viewboxes<2, XR session attiva, o gia' aborting. Mutex: durante il tour
  // walk/click-to-move/measure/hotspot/viewbox-select non agiscono.
  toggleCinematicTour(): void;
  // WS5 PR3.3c: snapshot stato cinematic per render iniziale (prima del
  // primo 'cinematic-state' event).
  getCinematicActive(): boolean;
  // WS5 PR5: ride snapshot (idle / ride_to_viewbox / ride_to_hotspot) +
  // elapsed + trigger. UI HUD usa per disable walk pill durante ride.
  getRideSnapshot(): RideSnapshot;
  // 4.K: WebXR (VR / AR) modal + session controls + getter. Modal
  // detection + session lifecycle are async; UI consumes snapshots
  // via 'xr-state' event. toggleXR is the convenience for the 'x'
  // shortcut / toolbar button (idle → openModal; modal-open →
  // closeModal; in-session → exitXR).
  toggleXR(): void;
  openXRModal(): void;
  closeXRModal(): void;
  enterXR(mode: XRMode): void;
  exitXR(): Promise<void>;
  getXRSnapshot(): XRSnapshot;
  // 4.M: device capabilities + walk-touch toast trigger.
  getDeviceCapabilities(): DeviceCapabilities;
  // Called by toolbar callback when the user clicks the walk button
  // on a touch device (button is aria-disabled but still clickable
  // for educational toast). Emits 'walk-error' reason='touch'.
  notifyUnavailableFeature(reason: 'walk-touch'): void;
  // 5.C: Studio Web author capture-camera. Returns an immutable
  // snapshot {cam_pos, cam_lookat, cam_fov} with finite-only values
  // and fov clamped to [10, 170]. Throws if the camera/controls
  // aren't wired yet (call only after 'viewer-ready').
  getCameraState(): {
    cam_pos: readonly [number, number, number];
    cam_lookat: readonly [number, number, number];
    cam_fov: number;
  };
  // 5.C: Studio Web author hotspot pos derivation. Casts a ray from
  // the viewport center forward against the loaded scene and returns
  // the first hit point as Vec3, or null if no hit / raycast not
  // available. Used to disable "Aggiungi hotspot" when there's no
  // model under the camera.
  raycastCenter(): readonly [number, number, number] | null;
  // Step 7: raycast da coordinate screen (canvas-relative, offsetX/Y).
  // Usato dal floorplan calibrator nello Studio editor per catturare
  // X/Z world quando l'autore clicca su un punto del modello 3D.
  raycastFromScreen(screenX: number, screenY: number): readonly [number, number, number] | null;
  // F8: hotspot click-to-place. Installs a single-shot canvas click handler
  // (same drag-vs-click pattern as measure/click-to-move): on the next valid
  // click it raycasts at the pointer and calls onPick(hit|null), then removes
  // itself. onPick receives null when the click misses the model (caller shows
  // feedback). Entering placement exits walk/measure/tour/click-to-move (mutex);
  // entering those modes (or dispose) cancels an active placement → onCancelled()
  // fires so the caller can reset its 'placing' UI state. Returns a cancel() to
  // abort placement (Annulla / tab switch / route change), which also fires onCancelled.
  beginHotspotPlacement(
    onPick: (pos: readonly [number, number, number] | null) => void,
    onCancelled: () => void,
  ): () => void;
  // F8: cancel an in-flight placement (no-op if none). Called by other
  // interaction modes (reverse mutex) and by the Studio floorplan capture.
  cancelHotspotPlacement(): void;
  // 5.C: Studio Web author CRUD sync. After create/update/delete via
  // the Studio API, call replaceHotspots/replacePresets so the
  // embedded viewer's sortedHotspots/sortedPresets cache stays in
  // sync (otherwise openHotspot/applyPreset would fail on new IDs).
  // Closes any popup/tour pointing to a removed entry.
  replaceHotspots(hotspots: readonly Hotspot[]): void;
  replacePresets(presets: readonly Preset[]): void;
}

interface InternalState {
  abortController: AbortController;
  unsubscribe: (() => void) | null;
  blobHandle: BlobUrlHandle | null;
  controls: OrbitControls | null;
  disposed: boolean;
  // Phase 6 — adaptive variant delivery state.
  manifest: import('../api/types.js').ModelManifest | null;
  variantOverride: import('../loader/variant-resolver.js').VariantOverride;
  // Retry attempt counter is persisted here so it survives the
  // error-transient → fetching-meta → ... cycle (the store state alone loses
  // it when RETRY moves out of error-transient).
  lastTransientAttempt: number;
  // 4.E: initial camera computed via adapter.computeFitCamera() when the
  // model finishes loading; used by resetView() to send the camera back.
  initialCamera: import('../viewer/adapter.js').CameraFit | null;
  // 4.E: perf HUD visibility (renderer also tracks; orchestrator authoritative).
  perfHudVisible: boolean;
  // 4.E: most recent ModelMeta filename for screenshot default name.
  lastFilename: string | null;
  // 4.E: cleanup handles for the bindings created in attachLoadedFeatures().
  loadedTeardown: (() => void)[];
  // 4.F: tour state-machine + animation ownership token. tourAbort gates which
  // flyToCamera invocation is "current"; manual applyPreset also uses it as a
  // local ownership token to avoid a race re-enabling controls during a
  // freshly-started tour transition.
  tourState: TourState;
  tourAbort: AbortController | null;
  sortedPresets: readonly Preset[];
  // 4.F: throttle ~10 hz emission of TICK no-op snapshots (dwell countdown).
  lastSnapshotAt: number;
  // 4.G: hotspots state + canonical sorted list + animation ownership token
  // separate from tourAbort (so an in-flight hotspot fly survives an
  // unrelated tour exit, and vice-versa).
  hotspotsState: HotspotsState;
  sortedHotspots: readonly Hotspot[];
  hotspotAbort: AbortController | null;
  // 4.H: floor plan minimap state. degenerateCalibration is computed
  // ONCE at scene-load and treated as immutable input; the reducer
  // never mutates it.
  floorplanState: FloorplanState;
  floorplan: FloorPlan | null;
  // Throttle the per-frame 'floorplan-position' emit to ~30 hz so the
  // ticker stays cheap even with N hotspots in the same loop.
  lastFloorplanEmit: number;
  // 4.J: measurements state + per-frame throttle + pointer counter
  // for deterministic point IDs (passed via event payload). The click
  // handler reference is kept so toggleMeasure can attach/detach it.
  measureState: MeasurementsState;
  lastMeasureProjEmit: number;
  measurePointSeq: number;
  measureClickHandler: ((ev: PointerEvent) => void) | null;
  // Step 7 #4 fix: paired pointerdown handler so we can detach on cleanup.
  measurePointerDownHandler?: (ev: PointerEvent) => void;
  // Inc D: post-FX user preference (toggle state). Effective render state is
  // derived (resolvePostFxSnapshot) from this + tour + pointerCoarse.
  postFxUserPreference: boolean;
  // Inc A: click-to-move handlers + in-flight glide abort controller.
  clickToMoveActive: boolean;
  clickToMoveDownHandler: ((ev: PointerEvent) => void) | null;
  clickToMoveUpHandler: ((ev: PointerEvent) => void) | null;
  clickToMoveAbort: AbortController | null;
  // F8: hotspot placement cleanup (detach listeners + reset cursor). Stored
  // like measure/click-to-move handlers so dispose() and the reverse mutex can
  // cancel an armed placement. null when no placement is active.
  hotspotPlacementCleanup: (() => void) | null;
  // 4.I1: walk mode (FPV) state. Pointer-lock + key/mouse listeners +
  // RAF subscription owned by the orchestrator; reset on EXIT.
  walkState: WalkState;
  walkKeysHeld: Set<string>;
  walkYaw: number;
  walkPitch: number;
  walkVY: number;
  walkLastTickMs: number;
  walkJumpHeld: boolean;
  walkPointerLockHandler: (() => void) | null;
  walkKeyDownHandler: ((ev: KeyboardEvent) => void) | null;
  walkKeyUpHandler: ((ev: KeyboardEvent) => void) | null;
  walkMouseMoveHandler: ((ev: MouseEvent) => void) | null;
  walkBlurHandler: (() => void) | null;
  walkUnsubFrame: (() => void) | null;
  // WS5 PR4a — Mobile FP touch state. Quando pointerCoarse=true e il
  // flag mobile-fp non e' off, runWalkEffects monta joystick + touch
  // controls al posto di pointer-lock/key/mouse handlers. walkTouchInput
  // viene letto da walkTick → computeVelocity ramo touch.
  walkTouchInput: WalkTouchInput | null;
  walkJoystick: JoystickController | null;
  walkTouchControls: TouchControls | null;
  // WS5 PR4a (Codex iter3 add#1 INCOMPLETE): modalita' di input INSTALLATA
  // al toggle walk ON. Stabile per la durata della sessione walk, NON
  // segue pointerCoarse live (Surface 2-in-1 hot-switch). Letta dal
  // walk-hud per scegliere overlay WASD vs touch hint coerente con i
  // listener effettivamente montati. null quando walk inattivo.
  walkInputMode: 'keyboard' | 'touch' | null;
  // 4.I1 Codex iter 1 code MINOR fix: incremented per pointer-lock
  // request so a stale Promise.reject from a previous entry doesn't
  // EXIT a fresh walk session (entry → exit → re-entry race).
  walkPointerLockSeq: number;
  // 4.K: WebXR (VR / AR) modal + session state. Adapter event
  // listeners are subscribed in attachLoadedFeatures so they survive
  // hot-reload of teardown/re-attach (see xrAdapterUnsub*). The
  // sequence guard `xrSessionSeq` invalidates the .catch of a stale
  // adapter.enterXR(mode) Promise after teardown / exitXR-while-
  // starting (Codex iter 4 plan MAJOR #1 fix).
  xrState: XRState;
  xrAdapterUnsubStarted: (() => void) | null;
  xrAdapterUnsubEnded: (() => void) | null;
  xrModalOpenerEl: HTMLElement | null;
  xrSessionSeq: number;
  // 4.M: device matcher subscription. Owned by orchestrator so the
  // unsubscribe runs in dispose() before the bootstrap tears down
  // the matcher itself.
  unsubDeviceMatcher: (() => void) | null;
  // WS5 PR3.2a: multi-viewbox state. Inizializzato a INITIAL_VIEWBOX_STATE,
  // popolato da `dispatchViewboxEvent({type:'LOAD',...})` dopo che il manifest
  // del modello e' stato fetched + normalizzato (decrypt offset se applicabile).
  // Walk state.currentViewboxArrayIdx e' tenuto sincronizzato via dispatch
  // walkEvent SET_VIEWBOX dentro dispatchViewboxEvent (single-source-of-truth).
  viewboxState: ViewboxState;
  // WS5 PR3.3c: cinematic auto-tour ownership. cinematicAbort gestisce
  // la sequenza fly+dwell (loop principale del sequencer). cinematicLandAbort
  // copre il fly finale di landing (separato per consentire takeover XR/
  // overview/applyPreset/startTour di cancellare ANCHE il land in volo
  // — Codex iter2 W3b). active e' boolean letto da snapshot getter +
  // riemesso via 'cinematic-state' event.
  cinematicAbort: AbortController | null;
  cinematicLandAbort: AbortController | null;
  cinematicActive: boolean;
  // WS5 PR5 — ride-mode + pin. rideState e' slice ortogonale a viewbox/walk.
  // Phase != 'idle' → walkTick si sospende (come per teleport gliding) +
  // selectViewbox no-op (mutex). Cooldown impedisce re-trigger immediato
  // edge auto trigger post-COMPLETE.
  rideState: RideState;
  // Tracking persistenza outward velocity per edge auto trigger.
  rideEdgeTracking: EdgeTrackingState;
  // pinsLayer = Group THREE che contiene main+halo Sprite per ogni viewbox.
  // Allocato in attachLoadedFeatures dopo dispatchViewboxEvent('LOAD');
  // dispose orchestrator rimuove + dispose textures shared.
  ridePinsLayer: THREE_PR5.Group | null;
  // Pair main+halo per pin per facilitare visibility update tick.
  ridePinsPairs: PinSpritePair[];
  // Click handler globale pin (canvas pointerup). Installato in
  // attachLoadedFeatures, rimosso in detachLoadedFeatures.
  ridePinClickHandler: ((ev: PointerEvent) => void) | null;
  // Feature flag URL `?ride_mode=0` → disabilita PR5 completamente.
  rideFeatureEnabled: boolean;
  // PR5 fix 2026-06-06: occlusion check DEFAULT OFF (pin sempre visibili
  // attraverso muri splat). Opt-in via `?ride_occlusion=1`.
  rideOcclusionEnabled: boolean;
}

// WS1 perf governor: cap del device-pixel-ratio per classe device. Il costo di
// rendering degli splat è dominato dal fill-rate (frammenti × pixel); su mobile
// un DPR 2-3× moltiplica il lavoro per nulla. Valori conservativi e tunabili.
const MAX_DPR_MOBILE = 1.25;
const MAX_DPR_DESKTOP = 2.0;

// WS4 golden standard: budget di splat ATTIVI per-device (1M mobile / 3M desktop),
// passato a SparkRenderer.lodSplatCount. Agisce solo sulle mesh paged (.rad) — innocuo
// per gli asset flat legacy (Spark applica il LOD solo a paged/lodSplats).
const LOD_SPLAT_COUNT_MOBILE = 1_000_000;
const LOD_SPLAT_COUNT_DESKTOP = 3_000_000;

function buildInitOptions(
  canvas: HTMLCanvasElement,
  embed: boolean,
  maxPixelRatio: number,
  lodSplatCount: number,
): InitOptions {
  if (embed) {
    return {
      canvas,
      webXRMode: 'none',
      sharedMemoryForWorkers: false,
      gpuAcceleratedSort: false,
      maxPixelRatio,
      lodSplatCount,
    };
  }
  return { canvas, webXRMode: 'none', maxPixelRatio, lodSplatCount };
}

// Inc D: sessionStorage access can throw in sandboxed iframes (SecurityError).
// Returns null when unavailable so post-FX persistence degrades gracefully.
function safeSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

function classifyMetaError(
  err: unknown,
  route: ParsedRoute,
): {
  type: 'ERROR_NOT_FOUND' | 'ERROR_EXPIRED' | 'ERROR_TRANSIENT';
  payload: Record<string, unknown>;
} {
  if (err instanceof ApiError) {
    if (err.code === 'not_found' || err.code === 'unauthorized' || err.code === 'forbidden') {
      return { type: 'ERROR_NOT_FOUND', payload: { route } };
    }
    if (err.code === 'gone_expired') {
      const expiresAt =
        typeof err.details?.['expires_at'] === 'number' ? (err.details['expires_at'] as number) : 0;
      return { type: 'ERROR_EXPIRED', payload: { route, expiresAt } };
    }
    return {
      type: 'ERROR_TRANSIENT',
      payload: { route, reason: err.code, canRetry: err.retryable },
    };
  }
  if (err instanceof NetworkError) {
    return { type: 'ERROR_TRANSIENT', payload: { route, reason: 'network', canRetry: true } };
  }
  return { type: 'ERROR_TRANSIENT', payload: { route, reason: 'unknown', canRetry: true } };
}

export function createOrchestrator(deps: OrchestratorDeps): Orchestrator {
  const events = new EventTarget();

  const internal: InternalState = {
    abortController: new AbortController(),
    unsubscribe: null,
    blobHandle: null,
    controls: null,
    disposed: false,
    manifest: null,
    variantOverride: null,
    lastTransientAttempt: 0,
    initialCamera: null,
    perfHudVisible: false,
    lastFilename: null,
    loadedTeardown: [],
    tourState: { kind: 'idle' },
    tourAbort: null,
    sortedPresets: [],
    lastSnapshotAt: 0,
    hotspotsState: { openIndex: -1, visible: false },
    sortedHotspots: [],
    hotspotAbort: null,
    floorplanState: { visible: false, imageError: false, degenerateCalibration: false },
    floorplan: null,
    lastFloorplanEmit: 0,
    measureState: { active: false, points: [], units: 'm' },
    lastMeasureProjEmit: 0,
    measurePointSeq: 0,
    measureClickHandler: null,
    postFxUserPreference: readPostFxPreference(safeSessionStorage()),
    clickToMoveActive: false,
    clickToMoveDownHandler: null,
    clickToMoveUpHandler: null,
    clickToMoveAbort: null,
    hotspotPlacementCleanup: null,
    walkState: {
      active: false,
      eyeHeight: EYE_HEIGHT,
      floorHeight: FLOOR_HEIGHT,
      // WS5 PR3.1: sincronizzato dall'orchestrator via dispatch SET_VIEWBOX
      // quando il viewbox state-machine emette LOAD/SELECT/TELEPORT_START.
      // null finche' nessuna scena con viewbox e' caricata.
      currentViewboxArrayIdx: null,
    },
    walkKeysHeld: new Set<string>(),
    walkYaw: 0,
    walkPitch: 0,
    walkVY: 0,
    walkLastTickMs: 0,
    walkJumpHeld: false,
    walkPointerLockHandler: null,
    walkKeyDownHandler: null,
    walkKeyUpHandler: null,
    walkMouseMoveHandler: null,
    walkBlurHandler: null,
    walkUnsubFrame: null,
    walkPointerLockSeq: 0,
    walkTouchInput: null,
    walkJoystick: null,
    walkTouchControls: null,
    walkInputMode: null,
    xrState: INITIAL_XR_STATE,
    xrAdapterUnsubStarted: null,
    xrAdapterUnsubEnded: null,
    xrModalOpenerEl: null,
    xrSessionSeq: 0,
    unsubDeviceMatcher: null,
    viewboxState: INITIAL_VIEWBOX_STATE,
    cinematicAbort: null,
    cinematicLandAbort: null,
    cinematicActive: false,
    // WS5 PR5 — ride-mode + pin init.
    rideState: INITIAL_RIDE_STATE,
    rideEdgeTracking: initEdgeTracking(),
    ridePinsLayer: null,
    ridePinsPairs: [],
    ridePinClickHandler: null,
    // Feature flag: `?ride_mode=0` URL param disabilita; default ON.
    rideFeatureEnabled: ((): boolean => {
      try {
        if (typeof window === 'undefined') return true;
        const sp = new URL(window.location.href).searchParams;
        return sp.get('ride_mode') !== '0';
      } catch {
        return true;
      }
    })(),
    // PR5 fix 2026-06-06 (post-utente bug walk no-pin): occlusion check
    // DEFAULT OFF — il raycast contro SplatMesh nasconde i pin in walk
    // first-person quando ci sono muri splat tra camera e pin (i pin
    // worldspace del Three.Sprite passano attraverso i muri di default,
    // Unity-style). Opt-in via URL param `?ride_occlusion=1` per riabilitare
    // quando ci saranno mesh collider reali (future Inc), o per scene
    // con headbox separati dove l'occlusion ha senso.
    rideOcclusionEnabled: ((): boolean => {
      try {
        if (typeof window === 'undefined') return false;
        const sp = new URL(window.location.href).searchParams;
        return sp.get('ride_occlusion') === '1';
      } catch {
        return false;
      }
    })(),
  };

  const emit = <K extends keyof OrchestratorEventDetailMap>(
    type: K,
    detail: OrchestratorEventDetailMap[K],
  ): void => {
    events.dispatchEvent(new CustomEvent(type, { detail }));
  };

  const revokeBlob = (): void => {
    if (internal.blobHandle !== null) {
      internal.blobHandle.revoke();
      internal.blobHandle = null;
    }
  };

  const bumpAttempt = (): number => {
    internal.lastTransientAttempt += 1;
    return internal.lastTransientAttempt;
  };

  const dispatchTransientError = (
    err: unknown,
    route: ParsedRoute | null,
    fallbackReason: string,
  ): void => {
    if (internal.disposed) return;
    if (route !== null) {
      const classified = classifyMetaError(err, route);
      if (classified.type === 'ERROR_NOT_FOUND') {
        deps.store.dispatch({ type: 'ERROR_NOT_FOUND', route });
        return;
      }
      if (classified.type === 'ERROR_EXPIRED') {
        deps.store.dispatch({
          type: 'ERROR_EXPIRED',
          route,
          expiresAt: classified.payload['expiresAt'] as number,
        });
        return;
      }
      deps.store.dispatch({
        type: 'ERROR_TRANSIENT',
        route,
        reason: (classified.payload['reason'] as string) ?? fallbackReason,
        canRetry: (classified.payload['canRetry'] as boolean) ?? true,
        attempt: bumpAttempt(),
      });
      return;
    }
    deps.store.dispatch({
      type: 'ERROR_TRANSIENT',
      route: null,
      reason: fallbackReason,
      canRetry: false,
      attempt: bumpAttempt(),
    });
  };

  const fetchMeta = async (route: ParsedRoute): Promise<void> => {
    const signal = internal.abortController.signal;
    try {
      // Phase 6 (parent plan §Fase 6 + Codex iter 2 NB#2): manifest call
      // separato. Fetch manifest first; if available pick variant per device
      // class via selectVariantForDevice; if 404/missing fall back to legacy
      // single-file fetchModel (no variant query). This keeps backward-compat
      // with pre-Phase-5 models that have no model_variants rows.
      let chosenVariant: 'hd' | 'sd' | 'rad' | undefined;
      let manifestWarning = '';
      try {
        const rawManifest = await deps.apiClient.fetchModelManifest(route.modelId, { signal });
        // WS5 PR3.2a: normalizzazione viewboxes (decrypt offset se applicabile).
        // shareToken=null finche' PR4 introduce share_link flow (oggi viewer
        // legge solo /api/model/:id/manifest pubblico → privacy_offset_hmac_v1
        // non dovrebbe mai apparire; se appare e' bug pipeline → fail-loud
        // dell'helper protegge dall'usare coordinate corrotte).
        const manifest = await normalizeManifestViewboxes(rawManifest, null);
        const deviceClass = detectDeviceClass(readDeviceClassInputs(deps.deviceMatcher.snapshot));
        // Punto 2 SOG full SH 2026-06-08 (Codex iter1 HIGH #1 fix): URL
        // `?variant=` ha precedenza su internal.variantOverride (settato da UI
        // switcher). Permette force RAD/SOG/HD/source per visual regression
        // Playwright (tests/e2e/visual-sog-vs-rad.spec.ts).
        const effectiveOverride = route.variantOverride ?? internal.variantOverride;
        const resolved = selectVariantForDevice(manifest, deviceClass, effectiveOverride);
        if (resolved !== null && resolved.variant.variant !== 'source') {
          chosenVariant = resolved.variant.variant;
        }
        manifestWarning = resolved?.warning ?? '';
        internal.manifest = manifest;
        // WS5 PR3.2a: LOAD viewbox state da manifest. viewboxes assente / [] →
        // scena legacy (firstPersonAvailable=false, fallback orbit). PR3.2b
        // userà questi dati per spawn FP-default + AABB clamp.
        const vbs = manifest.viewboxes ?? [];
        // Punto 2 SOG full SH 2026-06-08 (Codex iter1 HIGH #1 fix): URL
        // `?vb=<int>` ha precedenza su default_viewbox_index del manifest se
        // l'indice esiste nel set viewboxes (clamp set-based identico a
        // backend handlers/model-manifest.ts:181 fix Codex PR1 W1).
        const manifestDefaultIdx = manifest.default_viewbox_index ?? null;
        const urlIdx = route.viewboxIndex;
        const validIndices = new Set(vbs.map((v) => v.index));
        const defaultIdx =
          urlIdx !== null && validIndices.has(urlIdx) ? urlIdx : manifestDefaultIdx;
        dispatchViewboxEvent({ type: 'LOAD', viewboxes: vbs, defaultIdx });
      } catch (manifestErr) {
        if (isAbortError(manifestErr)) return;
        // Manifest 404 is expected for pre-Phase-5 models. Other errors
        // (5xx, network, normalize fail-loud su payload offset senza token)
        // → fallback legacy fetchModel + viewbox RESET (no FP).
        internal.manifest = null;
        dispatchViewboxEvent({ type: 'RESET' });
      }
      void manifestWarning; // reserved for future toolbar pill (Phase 6.3)

      const meta = await deps.apiClient.fetchModel(route.modelId, {
        signal,
        ...(chosenVariant !== undefined ? { variant: chosenVariant } : {}),
      });
      // Pre-hook expiry check (cheap, sync).
      if (meta.expires_at < Date.now()) {
        deps.store.dispatch({ type: 'ERROR_EXPIRED', route, expiresAt: meta.expires_at });
        return;
      }
      internal.lastFilename = meta.filename;

      // Step 7 Phase 0 — optional pre-load gate (mobile OOM guardrail).
      // Hook awaited can take arbitrary time (modal); guard post-await
      // for dispose/abort/expiry race conditions before dispatching
      // either META_FETCHED or LOAD_CANCELLED_BY_USER.
      if (deps.shouldProceedAfterMeta !== undefined) {
        let proceed: boolean;
        try {
          proceed = await deps.shouldProceedAfterMeta(meta, { signal });
        } catch (hookErr) {
          if (isAbortError(hookErr)) return;
          // Hook should never throw — log + treat as proceed (fail-open
          // so a buggy guardrail can't brick the viewer).
          console.warn('[orchestrator] shouldProceedAfterMeta threw:', hookErr);
          proceed = true;
        }
        // Post-await guards: orchestrator dispose, abort, or link
        // expiry while waiting for user response.
        if (internal.disposed || signal.aborted) return;
        if (meta.expires_at < Date.now()) {
          deps.store.dispatch({ type: 'ERROR_EXPIRED', route, expiresAt: meta.expires_at });
          return;
        }
        if (!proceed) {
          deps.store.dispatch({ type: 'LOAD_CANCELLED_BY_USER', route, meta });
          return;
        }
      }

      deps.store.dispatch({ type: 'META_FETCHED', route, meta });
    } catch (err) {
      if (isAbortError(err)) return;
      dispatchTransientError(err, route, 'meta-fetch-failed');
    }
  };

  const loadScene = async (route: ParsedRoute, meta: ModelMeta): Promise<void> => {
    const signal = internal.abortController.signal;

    const modelTask = (async (): Promise<void> => {
      // Revoke any prior blob (e.g. from a previous RETRY attempt) before
      // creating a new one — prevents one leaked blob per failed load.
      revokeBlob();

      // Phase 8 prod (2026-05-10): fallback SD→HD on mobile decode error.
      // SOG decode requires full WebGPU; iOS Safari's partial WebGPU
      // implementation rejects with a runtime exception that the orchestrator
      // classifies as reason:'unknown'. When the served variant is 'sd' AND
      // the adapter throws, retry once with the HD variant before failing.
      const isVariantMeta = (m: ModelMeta): m is ModelMetaVariant =>
        'variant_served' in m && m.variant_served !== undefined;
      const initialVariant = isVariantMeta(meta) ? meta.variant_served : null;

      const tryLoad = async (target: ModelMeta): Promise<void> => {
        // WS4: la variante `rad` (paged LOD-tree) NON va bufferizzata in blob (vanifica
        // lo streaming a Range). Si serve via proxy same-origin /rad/<rad_key>
        // (functions/rad), passando il bbox del manifest per inquadrare la camera
        // (getBoundingBox() di Spark lancia per le mesh paged).
        if (target.format === 'rad') {
          const radMeta = target as ModelMetaVariant;
          const radKey = radMeta.rad_key;
          // Boundary guard: la chiave arriva dal backend ma la interpoliamo in un path
          // URL → accetta solo segmenti URL-safe che terminano in .rad. Esclude ?,#,%,
          // e i dot-segment (`.` non e' nella classe → `../` viene rifiutato).
          if (radKey == null || !/^[\w\-/]+\.rad$/.test(radKey)) {
            throw new Error('variante rad con rad_key mancante o non valida');
          }
          await deps.adapter.loadSplatScene(`/rad/${radKey}`, {
            format: 'rad',
            ...(radMeta.bbox != null ? { bbox: radMeta.bbox } : {}),
          });
          return;
        }
        const handle = await loadModelAsBlobUrl(target.download_url, {
          signal,
          onProgress: (p) => {
            deps.store.dispatch({ type: 'MODEL_PROGRESS', loaded: p.loaded, total: p.total });
          },
        });
        internal.blobHandle = handle;
        // WS4 + Barnes fix: passa il manifest bbox anche per le mesh flat
        // (hd/sd/source). Il viewer lo usa per camera fit, sovrascrivendo il
        // Spark.getBoundingBox che includerebbe outlier sky (vedi
        // spark-adapter.getSplatBoundingBox).
        const flatMeta = isVariantMeta(target) ? target : null;
        await deps.adapter.loadSplatScene(handle.blobUrl, {
          format: target.format,
          ...(flatMeta?.bbox != null ? { bbox: flatMeta.bbox } : {}),
        });
      };

      try {
        await tryLoad(meta);
      } catch (err) {
        // Revoke the handle from the failed primary attempt before we try
        // the fallback. internal.blobHandle was set inside tryLoad.
        revokeBlob();

        if (isAbortError(err)) throw err;
        // Fallback variant: `rad` fallita → variante flat device-appropriate (auto);
        // `sd` fallita su iOS (decode SOG) → hd. Altrimenti propaga l'errore originale.
        const fallbackVariant: 'hd' | 'auto' | null =
          initialVariant === 'rad' ? 'auto' : initialVariant === 'sd' ? 'hd' : null;
        if (fallbackVariant === null) throw err;

        try {
          const fallbackMeta = await deps.apiClient.fetchModel(route.modelId, {
            signal,
            variant: fallbackVariant,
          });
          if (signal.aborted) throw err;
          console.warn(
            `[orchestrator] variante ${initialVariant} fallita, retry con ${fallbackVariant}`,
            err instanceof Error ? err.message : String(err),
          );
          await tryLoad(fallbackMeta);
        } catch (fallbackErr) {
          // If the fallback handle was created and adapter still threw,
          // revoke it so we don't leak.
          if (internal.blobHandle !== null) {
            internal.blobHandle.revoke();
            internal.blobHandle = null;
          }
          // Surface the ORIGINAL error to the user (the SD failure they
          // care about); fallback failure is informational only.
          if (isAbortError(fallbackErr)) throw fallbackErr;
          throw err;
        }
      }
    })();

    const metadataTask = fetchMetadataTolerant(deps.apiClient, route.modelId, {
      signal,
      onPartial: (p) => {
        if (p.key === 'hotspots') {
          deps.store.dispatch({ type: 'METADATA_PARTIAL', key: 'hotspots', value: p.value });
        } else if (p.key === 'presets') {
          deps.store.dispatch({ type: 'METADATA_PARTIAL', key: 'presets', value: p.value });
        } else {
          deps.store.dispatch({ type: 'METADATA_PARTIAL', key: 'floorplan', value: p.value });
        }
      },
    });

    try {
      const [_, metadataResult] = await Promise.all([modelTask, metadataTask]);
      if (internal.disposed) return;
      // Ensure all metadata keys are present in the store before SCENE_LOADED;
      // the transition guards on complete metadata.
      ensureMetadataPresent(deps.store, metadataResult.metadata);
      for (const w of metadataResult.warnings) {
        console.warn(`[orchestrator] metadata warning: ${w.key} -> ${w.reason}`);
      }
      // 4.F: cache sorted presets BEFORE SCENE_LOADED dispatch. Store
      // subscribers (renderer) fire synchronously and read getSortedPresets()
      // when mounting Loaded; if we sorted only inside attachLoadedFeatures
      // the first mount would see an empty list and the pill would never
      // appear (mountPresetPill returns a no-op for length === 0).
      internal.sortedPresets = sortPresets(metadataResult.metadata.presets);
      // 4.G: same race-fix pattern — sortedHotspots must be present at
      // the first Loaded mount so dots/strip render with the correct count.
      internal.sortedHotspots = sortHotspots(metadataResult.metadata.hotspots);
      // Step 7 fix: same race-fix as floorplan below — initialize
      // hotspotsState.visible BEFORE dispatch(SCENE_LOADED) so the
      // renderer's first sync mount sees visible=true. Previously this
      // happened in attachLoadedFeatures AFTER dispatch, which mounted
      // mountHotspotDots with snapshot.visible=false → wrap.style.display
      // ='none' → dots invisible until the user toggled 'h'. The state
      // machine + ticker NEVER re-emit hotspot-state when the silent
      // flip happens, so the UI was stuck nascosto until manual toggle.
      if (internal.sortedHotspots.length > 0) {
        internal.hotspotsState = { openIndex: -1, visible: true };
      }
      // 4.H: floor plan + calibration degeneracy cached BEFORE dispatch
      // (same race-fix pattern as sortedPresets/sortedHotspots). Reducer
      // treats degenerateCalibration as immutable input. visible defaults
      // to true whenever a floorplan is attached so the renderer's first
      // sync mount sees the minimap (we used to flip this in
      // attachLoadedFeatures AFTER dispatch, which left the UI seeded
      // with visible=false until the next event — Codex iter 1 MAJOR fix).
      const fp = metadataResult.metadata.floorplan;
      internal.floorplan = fp;
      internal.floorplanState = {
        visible: fp !== null,
        imageError: false,
        degenerateCalibration: fp !== null && isCalibrationDegenerate(fp.calibration),
      };
      deps.store.dispatch({ type: 'SCENE_LOADED' });
    } catch (err) {
      if (isAbortError(err)) return;
      dispatchTransientError(err, route, 'scene-load-failed');
      // 5.C: also emit viewer-failure so the Studio embedded bridge's
      // ready Promise rejects (otherwise the editor stays in
      // loading-model forever).
      const message = err instanceof Error ? err.message : 'scene-load-failed';
      emit('viewer-failure', { message });
      return;
    }
    // 5.C: OrbitControls setup is required for capture-camera correctness
    // (getCameraState reads controls.target). On failure we still wire
    // loaded features (hotspot/preset/floorplan all work without
    // controls in fullscreen viewer + tests use fake adapters) but
    // emit `viewer-failure` instead of `viewer-ready` so the Studio
    // embedded bridge rejects `ready` and the editor goes to model-error.
    // The fullscreen viewer (src/main.ts) ignores viewer-failure.
    const controlsReady = attachOrbitControls();
    attachLoadedFeatures();
    if (controlsReady) {
      emit('viewer-ready', {});
    } else {
      emit('viewer-failure', { message: 'orbit-controls-failed' });
    }
  };

  const attachOrbitControls = (): boolean => {
    if (internal.disposed) return false;
    try {
      const camera = deps.adapter.getCamera();
      const renderer = deps.adapter.getRenderer();
      internal.controls = createOrbitControls(camera, renderer.domElement);
      if (canSetBeforeRender(deps.adapter)) {
        const controls = internal.controls;
        // 4.I1 Codex iter 1 code MAJOR fix: skip update() when controls
        // are disabled (e.g. during walk mode FPV). mkkellogg's
        // OrbitControls.update() can drift the camera toward its stored
        // `target` even with enabled=false, fighting the per-frame
        // setCamera() walkTick performs.
        deps.adapter.setBeforeRender(() => {
          if (controls.enabled) controls.update();
        });
      }
      return true;
    } catch (err) {
      console.warn('[orchestrator] OrbitControls setup failed:', err);
      return false;
    }
  };

  // ============================================================
  // 4.F Tour wiring (camera presets pill + guided tour state machine)
  // ============================================================
  //
  // All mutations to internal.tourState pass through dispatchTourEvent so
  // effect scheduling (flyToCamera, abort, controls toggle, snapshot emit)
  // is deterministic. The pure SM (tourTransition) decides the next state;
  // runTransitionEffects fires the side effects that match the (prev, next,
  // event) triple. See STEP4F_PLAN.md "Unified dispatch + effect scheduler".

  const currentCamera = (): CameraTarget => {
    const camera = deps.adapter.getCamera();
    const target = internal.controls?.target;
    return {
      pos: [camera.position.x, camera.position.y, camera.position.z],
      lookAt: target ? [target.x, target.y, target.z] : [0, 0, 0],
      fov: camera.fov,
    };
  };

  // Inc B (Codex): fly camera MANUALE condiviso da applyPreset (ramo idle) e
  // showOverview. Pattern camera-ownership: cattura la camera corrente,
  // disabilita i controls, prende il token internal.tourAbort (abortendo il
  // fly precedente + un eventuale glide click-to-move), anima via flyToCamera
  // (reduced-motion → snap), chiama onComplete solo se ancora owner, e nel
  // finally riabilita i controls SOLO se nessun altro takeover (tour/walk/XR)
  // li possiede (stesse guardie della race-fix Inc A).
  const flyManualTo = async (
    to: CameraTarget,
    opts: { warnLabel: string; onComplete?: () => void },
  ): Promise<void> => {
    const cur = currentCamera();
    if (internal.controls !== null) internal.controls.enabled = false;
    internal.tourAbort?.abort();
    internal.clickToMoveAbort?.abort();
    const localCtrl = new AbortController();
    internal.tourAbort = localCtrl;
    try {
      const durationMs = deps.deviceMatcher.snapshot.reducedMotion ? 0 : TRANSITION_MS;
      await flyToCamera(deps.adapter, internal.controls, cur, to, {
        durationMs,
        signal: localCtrl.signal,
      });
      if (internal.tourAbort === localCtrl) opts.onComplete?.();
    } catch (err) {
      if (!isAbortError(err)) console.warn(`[orchestrator] ${opts.warnLabel} failed:`, err);
    } finally {
      if (
        internal.tourAbort === localCtrl &&
        internal.tourState.kind === 'idle' &&
        !internal.walkState.active &&
        internal.xrState.status !== 'starting' &&
        internal.xrState.status !== 'in-session' &&
        internal.controls !== null
      ) {
        internal.controls.enabled = true;
      }
    }
  };

  const presetCamera = (p: Preset): CameraTarget => ({
    pos: p.cam_pos as [number, number, number],
    lookAt: p.cam_lookat as [number, number, number],
    fov: p.cam_fov,
  });

  const currentTourIndex = (): number => {
    const s = internal.tourState;
    if (s.kind === 'dwelling') return s.index;
    if (s.kind === 'transitioning') return s.to;
    if (s.kind === 'paused') return s.index;
    return -1;
  };

  const nowFromEvent = (e: TourEvent): number => (e.type === 'EXIT' ? performance.now() : e.now);

  const maybeEmitSnapshot = (state: TourState, now: number): void => {
    if (state.kind === 'idle') return;
    if (now - internal.lastSnapshotAt < 100) return;
    internal.lastSnapshotAt = now;
    emit('tour-state', tourSnapshot(state, internal.sortedPresets, now));
  };

  // Inc D: resolve the effective post-FX state from user preference + tour +
  // device, push it to the adapter, and broadcast the snapshot. Idempotent.
  const applyPostFxState = (): void => {
    const snapshot = resolvePostFxSnapshot({
      userPreference: internal.postFxUserPreference,
      tourActive: internal.tourState.kind !== 'idle',
      pointerCoarse: deps.deviceMatcher.snapshot.pointerCoarse,
    });
    if (canSetPostFx(deps.adapter)) {
      deps.adapter.setPostFx(
        snapshot.effective,
        snapshot.effective ? DEFAULT_POST_FX_OPTIONS : undefined,
      );
    }
    emit('post-fx-state', snapshot);
  };

  const runTransitionEffects = (prev: TourState, next: TourState, event: TourEvent): void => {
    // Step 1: abort any active flyTo when starting a NEW transitioning, or
    // when leaving the active state via PAUSE/EXIT.
    const newTransitionStarting =
      next.kind === 'transitioning' &&
      !(prev.kind === 'transitioning' && prev.to === next.to && prev.startedAt === next.startedAt);
    if (newTransitionStarting || next.kind === 'idle' || next.kind === 'paused') {
      internal.tourAbort?.abort();
      internal.tourAbort = null;
    }

    // Step 2: kick off the new flyToCamera. RESUME shrinks duration to
    // remainingMs so the animation ends exactly when the SM exits
    // transitioning; START/GOTO/end-of-dwell use TRANSITION_MS full.
    if (newTransitionStarting && next.kind === 'transitioning') {
      const fromCam = currentCamera();
      const toCam = presetCamera(internal.sortedPresets[next.to] as Preset);
      const isResume = event.type === 'RESUME';
      // 4.M: prefers-reduced-motion → snap immediate (durationMs=0).
      const reducedMotion = deps.deviceMatcher.snapshot.reducedMotion;
      const durationMs = reducedMotion
        ? 0
        : isResume &&
            prev.kind === 'paused' &&
            prev.pausedFromKind === 'transitioning' &&
            prev.index === next.to
          ? prev.remainingMs
          : TRANSITION_MS;
      if (internal.controls !== null) internal.controls.enabled = false;
      const controller = new AbortController();
      internal.tourAbort = controller;
      void flyToCamera(deps.adapter, internal.controls, fromCam, toCam, {
        durationMs,
        signal: controller.signal,
      })
        .catch((err) => {
          if (!isAbortError(err)) console.warn('[tour] flyTo failed:', err);
        })
        .finally(() => {
          // Re-enable controls only if WE are still the owner AND SM has
          // settled into dwelling for THIS index. Otherwise a newer transition
          // owns controls (or tour exited) — leave them alone.
          if (
            internal.tourAbort === controller &&
            internal.tourState.kind === 'dwelling' &&
            internal.tourState.index === next.to &&
            internal.controls !== null
          ) {
            internal.controls.enabled = true;
          }
        });
    }

    // Step 3: idempotent re-enable on dwelling/idle (catch-all).
    if ((next.kind === 'dwelling' || next.kind === 'idle') && internal.controls !== null) {
      internal.controls.enabled = true;
    }

    // Inc D: the tour forces post-FX ON while active and restores the user's
    // preference on exit. Re-derive only when the idle-ness actually flips,
    // so START (idle→…) and EXIT (…→idle) toggle the effective state.
    if ((prev.kind === 'idle') !== (next.kind === 'idle')) {
      applyPostFxState();
    }
  };

  // ============================================================
  // 4.G Hotspots wiring (dot scatter + popup card + strip)
  // ============================================================
  //
  // Mirrors the 4.F dispatch pattern: a single helper mutates state, fires
  // side effects (flyTo + abort + controls toggle), and emits the snapshot.
  // Hotspot ownership uses a separate AbortController (`internal.hotspotAbort`)
  // so an in-flight tour fly survives an unrelated hotspot exit and vice-versa.

  const runHotspotEffects = (
    prev: HotspotsState,
    next: HotspotsState,
    _event: HotspotsEvent,
  ): void => {
    const isFlyingToNew = next.openIndex !== -1 && next.openIndex !== prev.openIndex;
    if (isFlyingToNew) {
      // Tour ticker would otherwise resume control over the camera. Exit
      // before fly so the hotspot animation owns the controls cleanly.
      if (internal.tourState.kind !== 'idle') {
        dispatchTourEvent({ type: 'EXIT' });
      }
      internal.hotspotAbort?.abort();
      const target = internal.sortedHotspots[next.openIndex];
      if (target === undefined) return;
      const cur = currentCamera();
      const to: CameraTarget = {
        pos: target.cam_pos as [number, number, number],
        lookAt: target.cam_lookat as [number, number, number],
        fov: target.cam_fov,
      };
      if (internal.controls !== null) internal.controls.enabled = false;
      const ctrl = new AbortController();
      internal.hotspotAbort = ctrl;
      // 4.M: prefers-reduced-motion → snap immediate.
      const hotspotDuration = deps.deviceMatcher.snapshot.reducedMotion ? 0 : TRANSITION_MS;
      void flyToCamera(deps.adapter, internal.controls, cur, to, {
        durationMs: hotspotDuration,
        signal: ctrl.signal,
      })
        .catch((err) => {
          if (!isAbortError(err)) console.warn('[hotspot] flyTo failed:', err);
        })
        .finally(() => {
          // Token check: only re-enable controls if we are still the active
          // owner (a newer hotspot fly or a closeHotspot may have aborted us).
          if (internal.hotspotAbort === ctrl && internal.controls !== null) {
            internal.controls.enabled = true;
          }
        });
    }
    // Closing → abort any in-flight fly; user keeps the current view (no
    // camera revert).
    if (next.openIndex === -1 && prev.openIndex !== -1) {
      internal.hotspotAbort?.abort();
      internal.hotspotAbort = null;
      // WS5 PR3.3c (Codex iter3): NON riabilitare controls se cinematic owns
      // ancora la camera. Il finally cinematic gestira' la re-enable quando
      // il tour finisce.
      if (internal.controls !== null && !internal.cinematicActive) {
        internal.controls.enabled = true;
      }
    }
  };

  const dispatchHotspotEvent = (event: HotspotsEvent): void => {
    if (internal.disposed) return;
    const prev = internal.hotspotsState;
    const next = hotspotsTransition(prev, event, internal.sortedHotspots.length);
    if (next === prev) return;
    internal.hotspotsState = next;
    runHotspotEffects(prev, next, event);
    emit('hotspot-state', hotspotsSnapshot(next, internal.sortedHotspots));
  };

  // ============================================================
  // 4.H Floor plan minimap wiring
  // ============================================================
  //
  // Same dispatch pattern as 4.F/4.G: a single helper mutates state and
  // emits the snapshot. Teleport is a side effect (not an event in the
  // SM) because it changes the viewer camera, not the minimap state —
  // it's modeled the same way as resetView (4.E) and applyPreset idle
  // path (4.F).

  const dispatchFloorplanEvent = (event: FloorplanEvent): void => {
    if (internal.disposed) return;
    const prev = internal.floorplanState;
    const next = floorplanTransition(prev, event);
    if (next === prev) return;
    internal.floorplanState = next;
    emit('floorplan-state', floorplanSnapshot(next));
  };

  const teleportOnFloorplan = (worldXZ: readonly [number, number]): void => {
    if (internal.disposed) return;
    if (internal.floorplan === null) return;
    if (internal.floorplanState.degenerateCalibration) return; // ignored on degenerate
    // UX guard mirrored from hotspot.openHotspot — a hard camera shift
    // makes the popup point to a stale view.
    if (internal.tourState.kind !== 'idle') {
      dispatchTourEvent({ type: 'EXIT' });
    }
    if (internal.hotspotsState.openIndex !== -1) {
      dispatchHotspotEvent({ type: 'CLOSE' });
    }
    let cur: CameraTarget;
    try {
      cur = currentCamera();
    } catch (err) {
      console.warn('[floorplan] currentCamera failed:', err);
      return;
    }
    const offset: [number, number, number] = [
      cur.lookAt[0] - cur.pos[0],
      cur.lookAt[1] - cur.pos[1],
      cur.lookAt[2] - cur.pos[2],
    ];
    const newPos: [number, number, number] = [worldXZ[0], cur.pos[1], worldXZ[1]];
    const newLookAt: [number, number, number] = [
      newPos[0] + offset[0],
      newPos[1] + offset[1],
      newPos[2] + offset[2],
    ];
    try {
      setCameraDispatch(deps.adapter, { pos: newPos, lookAt: newLookAt, fov: cur.fov });
      if (internal.controls !== null) {
        internal.controls.target.set(newLookAt[0], newLookAt[1], newLookAt[2]);
        internal.controls.update();
      }
    } catch (err) {
      console.warn('[floorplan] teleport failed:', err);
    }
  };

  const dispatchTourEvent = (event: TourEvent): void => {
    if (internal.disposed) return;
    const prev = internal.tourState;
    const next = tourTransition(prev, event, internal.sortedPresets.length);
    if (next === prev) {
      if (event.type === 'TICK') maybeEmitSnapshot(prev, event.now);
      return;
    }
    internal.tourState = next;
    runTransitionEffects(prev, next, event);
    emit('tour-state', tourSnapshot(next, internal.sortedPresets, nowFromEvent(event)));
  };

  // ============================================================
  // 4.J Measurements wiring
  // ============================================================
  //
  // Same dispatch + effects pattern as 4.F/4.G/4.H. Side effects on the
  // active flip (install/remove canvas click handler, exit tour first)
  // live in runMeasurementEffects so they're driven by the toggle, not
  // by the first click — see plan iter 1 MAJOR #3 fix.

  // Step 7 #4 fix: distinguish "click pick" from "drag rotate". Without
  // this every release of the mouse button after an OrbitControls drag
  // adds a measure point — the canvas DOM 'click' event fires even for
  // small drag deltas. Industry-standard golden pattern (Matterport,
  // Sketchfab, Polycam, Esri ArcGIS): track pointerdown coordinates,
  // only pick on pointerup if the cursor moved less than ~5px.
  const MEASURE_DRAG_THRESHOLD_PX = 5;
  const installMeasureClickHandler = (): void => {
    if (internal.measureClickHandler !== null) return;
    let downX = 0;
    let downY = 0;
    let downValid = false;
    const onDown = (ev: PointerEvent): void => {
      // Only consider primary button (left click / single touch). On
      // mouse: button 0. On touch/pen: ev.button is also 0.
      if (ev.button !== 0) {
        downValid = false;
        return;
      }
      downX = ev.clientX;
      downY = ev.clientY;
      downValid = true;
    };
    const onUp = (ev: PointerEvent): void => {
      if (!downValid) return;
      downValid = false;
      const dx = ev.clientX - downX;
      const dy = ev.clientY - downY;
      const moved = Math.hypot(dx, dy);
      if (moved >= MEASURE_DRAG_THRESHOLD_PX) {
        // Treat as drag (orbit / pan); do not pick.
        return;
      }
      // offsetX/Y on pointerup is reliable — translates client coords to
      // canvas-relative coords used by the raycaster.
      api.addMeasurePoint(ev.offsetX, ev.offsetY);
    };
    internal.measureClickHandler = onUp;
    internal.measurePointerDownHandler = onDown;
    deps.canvas.addEventListener('pointerdown', onDown);
    deps.canvas.addEventListener('pointerup', onUp);
    deps.canvas.style.cursor = 'crosshair';
  };

  const removeMeasureClickHandler = (): void => {
    if (internal.measureClickHandler === null) return;
    deps.canvas.removeEventListener('pointerup', internal.measureClickHandler);
    if (internal.measurePointerDownHandler !== undefined) {
      deps.canvas.removeEventListener('pointerdown', internal.measurePointerDownHandler);
      delete internal.measurePointerDownHandler;
    }
    internal.measureClickHandler = null;
    deps.canvas.style.cursor = '';
  };

  // ============================================================
  // Inc A — Click-to-move inside-view (Matterport-style)
  // ============================================================
  // Toggle-driven (mutex con walk/measure/tour). Riusa il pattern measure
  // click-vs-drag (soglia 5px) così un drag OrbitControls NON innesca uno
  // spostamento. Math pura del target in walk/click-to-move.ts.
  const CLICK_TO_MOVE_DRAG_THRESHOLD_PX = 5;

  const moveToScreenPoint = (offsetX: number, offsetY: number): void => {
    if (!internal.clickToMoveActive || internal.disposed) return;
    // Gating: mai durante una sessione XR (attiva o in avvio); serve controls
    // (flyToCamera aggiorna controls.target e currentCamera lo legge).
    if (internal.xrState.status === 'in-session' || internal.xrState.status === 'starting') return;
    // Non avviare un glide durante un tour (eviterebbe un secondo flyToCamera
    // in conflitto con quello del tour). startTour esce comunque da
    // click-to-move; questo è il guard difensivo.
    if (internal.tourState.kind !== 'idle') return;
    // controls.enabled === false ⇒ una camera-animation possiede già la camera
    // (es. fly del preset manuale [applyPreset], transizioni tour) → niente
    // glide click-to-move in parallelo. A fine fly i controls vengono riabilitati.
    if (internal.controls === null || !internal.controls.enabled) return;
    const hit = api.raycastFromScreen(offsetX, offsetY);
    if (hit === null) return;
    const from = currentCamera();
    const target = computeMoveTarget(hit, from, DEFAULT_CLICK_TO_MOVE);
    if (target === null) return; // surface-filter: non è pavimento → no-op
    internal.clickToMoveAbort?.abort();
    const abort = new AbortController();
    internal.clickToMoveAbort = abort;
    const durationMs = moveDurationMs(
      deps.deviceMatcher.snapshot.reducedMotion,
      DEFAULT_CLICK_TO_MOVE,
    );
    void flyToCamera(deps.adapter, internal.controls, from, target, {
      durationMs,
      signal: abort.signal,
    }).catch((err) => {
      if (!isAbortError(err)) console.warn('[click-to-move] glide failed:', err);
    });
  };

  const installClickToMoveHandler = (): void => {
    if (internal.clickToMoveUpHandler !== null) return;
    let downX = 0;
    let downY = 0;
    let downValid = false;
    const onDown = (ev: PointerEvent): void => {
      if (ev.button !== 0) {
        downValid = false;
        return;
      }
      downX = ev.clientX;
      downY = ev.clientY;
      downValid = true;
    };
    const onUp = (ev: PointerEvent): void => {
      if (!downValid) return;
      downValid = false;
      if (Math.hypot(ev.clientX - downX, ev.clientY - downY) >= CLICK_TO_MOVE_DRAG_THRESHOLD_PX) {
        return; // drag = orbit/pan, non uno spostamento
      }
      moveToScreenPoint(ev.offsetX, ev.offsetY);
    };
    internal.clickToMoveDownHandler = onDown;
    internal.clickToMoveUpHandler = onUp;
    deps.canvas.addEventListener('pointerdown', onDown);
    deps.canvas.addEventListener('pointerup', onUp);
    deps.canvas.style.cursor = 'pointer';
  };

  const removeClickToMoveHandler = (): void => {
    if (internal.clickToMoveUpHandler !== null) {
      deps.canvas.removeEventListener('pointerup', internal.clickToMoveUpHandler);
      internal.clickToMoveUpHandler = null;
    }
    if (internal.clickToMoveDownHandler !== null) {
      deps.canvas.removeEventListener('pointerdown', internal.clickToMoveDownHandler);
      internal.clickToMoveDownHandler = null;
    }
    if (internal.clickToMoveAbort !== null) {
      internal.clickToMoveAbort.abort();
      internal.clickToMoveAbort = null;
    }
    deps.canvas.style.cursor = '';
  };

  const exitClickToMoveIfActive = (): void => {
    if (!internal.clickToMoveActive) return;
    internal.clickToMoveActive = false;
    removeClickToMoveHandler();
  };

  // ============================================================
  // F8 — Hotspot click-to-place (single-shot pick on the model)
  // ============================================================
  // Mirror del pattern measure/click-to-move (click-vs-drag soglia 5px,
  // offsetX/Y, crosshair, cleanup). UN click valido → raycastFromScreen →
  // onPick(hit|null) → si auto-rimuove. Reverse mutex: gli altri modi di
  // interazione chiamano cancelHotspotPlacementIfActive() all'ingresso.
  const HOTSPOT_PLACE_DRAG_THRESHOLD_PX = 5;

  const cancelHotspotPlacementIfActive = (): void => {
    internal.hotspotPlacementCleanup?.();
  };

  const installHotspotPlacementHandler = (
    onPick: (pos: readonly [number, number, number] | null) => void,
    // Codex code-review HIGH: la cancellazione out-of-band (reverse mutex /
    // dispose / Annulla) deve avvisare il caller, altrimenti lo stato author
    // resta bloccato in 'placing' col listener già staccato.
    onCancelled: () => void,
  ): void => {
    let downX = 0;
    let downY = 0;
    let downValid = false;
    const onDown = (ev: PointerEvent): void => {
      if (ev.button !== 0) {
        downValid = false;
        return;
      }
      downX = ev.clientX;
      downY = ev.clientY;
      downValid = true;
    };
    const detach = (): void => {
      deps.canvas.removeEventListener('pointerdown', onDown);
      deps.canvas.removeEventListener('pointerup', onUp);
      deps.canvas.style.cursor = '';
      internal.hotspotPlacementCleanup = null;
    };
    const onUp = (ev: PointerEvent): void => {
      if (!downValid) return;
      downValid = false;
      if (Math.hypot(ev.clientX - downX, ev.clientY - downY) >= HOTSPOT_PLACE_DRAG_THRESHOLD_PX) {
        return; // drag = orbit/pan, non un pick (puoi inquadrare poi cliccare)
      }
      const hit = api.raycastFromScreen(ev.offsetX, ev.offsetY);
      detach(); // PICK: stacca PRIMA del callback (single-shot), niente onCancelled
      onPick(hit); // hit === null ⇒ click fuori dal modello → il caller dà feedback
    };
    // hotspotPlacementCleanup = percorso CANCELLAZIONE (reverse mutex / dispose /
    // Annulla): stacca + notifica il caller per resettare lo stato da 'placing'.
    internal.hotspotPlacementCleanup = (): void => {
      detach();
      onCancelled();
    };
    deps.canvas.addEventListener('pointerdown', onDown);
    deps.canvas.addEventListener('pointerup', onUp);
    deps.canvas.style.cursor = 'crosshair';
  };

  // Inc A hardening (Codex Opzione C): un fly MANUALE di applyPreset usa
  // `tourAbort` come token pur con `tourState` idle. Se un takeover (walk/XR)
  // prende la camera mentre quel fly è in volo, va abortito e il token
  // liberato, altrimenti il fly continua a fare setCamera (judder) e il suo
  // `finally` può riabilitare i controls sotto walk/XR. Agisce SOLO sul fly
  // manuale (tourState idle): i tour reali (non-idle) restano gestiti da
  // dispatchTourEvent(EXIT) + runTransitionEffects.
  const abortManualCameraFly = (): void => {
    if (internal.tourState.kind === 'idle' && internal.tourAbort !== null) {
      internal.tourAbort.abort();
      internal.tourAbort = null;
    }
  };

  const runMeasurementEffects = (
    prev: MeasurementsState,
    next: MeasurementsState,
    _event: MeasurementsEvent,
  ): void => {
    if (!prev.active && next.active) {
      // Tour ticker would otherwise keep moving the camera under the
      // user's first click. Exit tour BEFORE attaching the click handler.
      if (internal.tourState.kind !== 'idle') {
        dispatchTourEvent({ type: 'EXIT' });
      }
      installMeasureClickHandler();
    }
    if (prev.active && !next.active) {
      removeMeasureClickHandler();
    }
  };

  const dispatchMeasurementEvent = (event: MeasurementsEvent): void => {
    if (internal.disposed) return;
    const prev = internal.measureState;
    const next = measurementsTransition(prev, event);
    if (next === prev) return;
    internal.measureState = next;
    runMeasurementEffects(prev, next, event);
    emit('measurement-state', measurementsSnapshot(next));
  };

  // ============================================================
  // 4.I1 Walk Mode (FPV) wiring
  // ============================================================
  //
  // dispatchWalkEvent funnels every state mutation; runWalkEffects
  // owns side effects (pointer-lock attach/detach, key/mouse listener
  // install, frame ticker subscription, OrbitControls toggle, exit
  // tour/hotspot/measure first). Pointer-lock is fragile (browser
  // unsupported, iframe permission denial) — we handle both sync and
  // async failures by dispatching a nested EXIT, and the outer
  // dispatch guards against emitting a stale active=true snapshot.

  const installWalkInputHandlers = (): void => {
    if (internal.walkKeyDownHandler !== null) return;
    const onKeyDown = (ev: KeyboardEvent): void => {
      const key = normalizeWalkKey(ev.key);
      // Only consume when walk is active; SHORTCUTS handler still owns 'v'.
      if (!internal.walkState.active) return;
      // Prevent default for movement keys + Space so the page doesn't scroll.
      if (key === 'w' || key === 'a' || key === 's' || key === 'd' || key === ' ') {
        ev.preventDefault();
      }
      internal.walkKeysHeld.add(key);
    };
    const onKeyUp = (ev: KeyboardEvent): void => {
      const key = normalizeWalkKey(ev.key);
      if (!internal.walkState.active) return;
      internal.walkKeysHeld.delete(key);
      if (key === ' ') internal.walkJumpHeld = false;
    };
    const onMouseMove = (ev: MouseEvent): void => {
      if (!internal.walkState.active) return;
      // Only consume mouse delta when pointer is actually locked.
      if (document.pointerLockElement !== deps.canvas) return;
      // Sub-task 1.2 (2026-06-05): durante un teleport glide viewbox-to-viewbox
      // NON aggiornare yaw/pitch da mouse, altrimenti l'utente "guarda altrove"
      // nel mezzo del glide e il sync post-TELEPORT_COMPLETE viene perso al
      // primo onMouseMove successivo.
      if (internal.viewboxState.teleportPhase === 'gliding') return;
      // movementX positive = mouse moved right → user wants to look right
      // → in our convention (yaw positive = LEFT), yaw decreases.
      internal.walkYaw -= ev.movementX * MOUSE_SENSITIVITY;
      internal.walkPitch = clampPitch(internal.walkPitch - ev.movementY * MOUSE_SENSITIVITY);
    };
    const onPointerLockChange = (): void => {
      // Browser ESC / tab switch / programmatic exitPointerLock all fire
      // pointerlockchange. If we lost the lock while still active, dispatch
      // EXIT so the SM rolls back cleanly.
      if (document.pointerLockElement !== deps.canvas && internal.walkState.active) {
        dispatchWalkEvent({ type: 'EXIT' });
      }
    };
    const onBlur = (): void => {
      // Window lost focus (alt-tab, cmd-tab) — keyup events won't arrive
      // for keys held down at the moment of blur. Clear the held set so
      // the next keydown starts from a clean state.
      internal.walkKeysHeld.clear();
      internal.walkJumpHeld = false;
    };
    internal.walkKeyDownHandler = onKeyDown;
    internal.walkKeyUpHandler = onKeyUp;
    internal.walkMouseMoveHandler = onMouseMove;
    internal.walkPointerLockHandler = onPointerLockChange;
    internal.walkBlurHandler = onBlur;
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('pointerlockchange', onPointerLockChange);
    window.addEventListener('blur', onBlur);
  };

  const removeWalkInputHandlers = (): void => {
    if (internal.walkKeyDownHandler !== null) {
      window.removeEventListener('keydown', internal.walkKeyDownHandler);
      internal.walkKeyDownHandler = null;
    }
    if (internal.walkKeyUpHandler !== null) {
      window.removeEventListener('keyup', internal.walkKeyUpHandler);
      internal.walkKeyUpHandler = null;
    }
    if (internal.walkMouseMoveHandler !== null) {
      document.removeEventListener('mousemove', internal.walkMouseMoveHandler);
      internal.walkMouseMoveHandler = null;
    }
    if (internal.walkPointerLockHandler !== null) {
      document.removeEventListener('pointerlockchange', internal.walkPointerLockHandler);
      internal.walkPointerLockHandler = null;
    }
    if (internal.walkBlurHandler !== null) {
      window.removeEventListener('blur', internal.walkBlurHandler);
      internal.walkBlurHandler = null;
    }
    internal.walkKeysHeld.clear();
    internal.walkJumpHeld = false;
  };

  // WS5 PR4a — mount joystick + drag-rotate/pinch-FOV su pointerCoarse.
  // Sostituisce installWalkInputHandlers nel ramo mobile FP (NO pointer
  // lock + NO key/mouse listeners). joystick e' parented al layer canvas
  // (parentElement) cosi' resta visibile in fullscreen.
  const installWalkTouchHandlers = (): void => {
    if (internal.walkJoystick !== null || internal.walkTouchControls !== null) return;
    // Codex CRIT #5: parent = canvas.parentElement (canvas layer) → resta
    // dentro il fullscreen target del wrap.
    const parent = deps.canvas.parentElement ?? document.body;
    internal.walkJoystick = createJoystickController(parent, {
      ariaLabel: i18nT('viewer.joystick.aria_label'),
      onUpdate: (input) => {
        // Race guard: se walk e' stato disattivato (es. teardown async),
        // ignora aggiornamenti residui del joystick.
        if (!internal.walkState.active) return;
        if (internal.cinematicActive) return;
        internal.walkTouchInput = input;
      },
    });
    internal.walkTouchControls = createTouchControls(
      deps.canvas,
      {
        onRotate: ({ deltaYaw, deltaPitch }) => {
          if (!internal.walkState.active) return;
          if (internal.cinematicActive) return;
          // Sub-task 1.2 (2026-06-05): mirror del guard onMouseMove. Touch
          // rotate durante glide → ignorato.
          if (internal.viewboxState.teleportPhase === 'gliding') return;
          internal.walkYaw += deltaYaw;
          internal.walkPitch = clampPitch(internal.walkPitch + deltaPitch);
        },
        onPinch: ({ fovDeg }) => {
          if (!internal.walkState.active) return;
          if (internal.cinematicActive) return;
          try {
            const cam = deps.adapter.getCamera();
            const fwd = forwardVector(internal.walkYaw, internal.walkPitch);
            setCameraDispatch(deps.adapter, {
              pos: [cam.position.x, cam.position.y, cam.position.z],
              lookAt: [
                cam.position.x + fwd[0],
                cam.position.y + fwd[1],
                cam.position.z + fwd[2],
              ],
              fov: fovDeg,
            });
          } catch (err) {
            console.warn('[walk] pinch setCamera failed:', err);
          }
        },
      },
      {
        getCurrentFovDeg: (): number => {
          try {
            return deps.adapter.getCamera().fov;
          } catch {
            return 55;
          }
        },
        getJoystickRect: () => internal.walkJoystick?.getRect() ?? null,
      },
    );
    // Codex WARN #7: blocca scroll/zoom nativo del documento sul canvas
    // mentre walk mobile attivo.
    deps.canvas.style.touchAction = 'none';
  };

  const removeWalkTouchHandlers = (): void => {
    if (internal.walkJoystick !== null) {
      internal.walkJoystick.dispose();
      internal.walkJoystick = null;
    }
    if (internal.walkTouchControls !== null) {
      internal.walkTouchControls.dispose();
      internal.walkTouchControls = null;
    }
    internal.walkTouchInput = null;
    deps.canvas.style.touchAction = '';
  };

  const snapCameraToWalkStart = (): void => {
    try {
      const cam = deps.adapter.getCamera();

      // WS5 PR5 fix 2026-06-05 (Codex INDIPENDENT bug2): se c'e' una viewbox
      // corrente, spawn camera al `default_camera.pos` di quella viewbox (=
      // spawn point calibrato Seurat). Pre-fix: preserve XZ camera corrente +
      // change Y, ma se camera era in overview dollhouse (zoom-out fuori
      // edificio), il walk spawnava fuori dall'appartamento. Con default_camera.pos
      // si garantisce spawn DENTRO la stanza corretta. Yaw/pitch/fov dal manifest.
      const vbIdx = internal.viewboxState.currentArrayIdx;
      let spawnX = cam.position.x;
      let spawnZ = cam.position.z;
      let yaw = 0;
      let pitch = 0;
      let fov = cam.fov;
      let useViewboxSpawn = false;
      if (vbIdx !== null) {
        const vb = internal.viewboxState.available[vbIdx];
        if (vb !== undefined && Number.isFinite(vb.default_camera.pos[0])) {
          spawnX = vb.default_camera.pos[0];
          spawnZ = vb.default_camera.pos[2];
          yaw = Number.isFinite(vb.default_camera.yaw) ? vb.default_camera.yaw : 0;
          pitch = Number.isFinite(vb.default_camera.pitch) ? vb.default_camera.pitch : 0;
          fov = Number.isFinite(vb.default_camera.fov)
            ? (vb.default_camera.fov * 180) / Math.PI
            : cam.fov;
          useViewboxSpawn = true;
        }
      }
      if (!useViewboxSpawn) {
        // Fallback legacy: scene senza viewbox → preserve XZ + Y a eye height
        // + yaw da camera quaternion (= heading corrente, no spawn point).
        const q = cam.quaternion;
        yaw = Math.atan2(2 * (q.w * q.y + q.x * q.z), 1 - 2 * (q.y * q.y + q.z * q.z));
      }

      internal.walkYaw = yaw;
      internal.walkPitch = clampPitch(pitch);
      internal.walkVY = 0;
      internal.walkLastTickMs = performance.now();
      internal.walkJumpHeld = false;
      const newY = internal.walkState.floorHeight + internal.walkState.eyeHeight;
      const fwd = forwardVector(internal.walkYaw, internal.walkPitch);
      setCameraDispatch(deps.adapter, {
        pos: [spawnX, newY, spawnZ],
        lookAt: [spawnX + fwd[0], newY + fwd[1], spawnZ + fwd[2]],
        fov,
      });
    } catch (err) {
      console.warn('[walk] snap to start failed:', err);
    }
  };

  const walkTick = (now: number): void => {
    if (!internal.walkState.active) return;
    // WS5 PR3.2b: durante un teleport viewbox-to-viewbox (flyToCamera glide),
    // walkTick NON deve sovrascrivere setCamera ogni frame. Il viewbox state
    // machine settera' teleportPhase='idle' al completamento → walk riprende
    // dal punto finale del glide.
    if (internal.viewboxState.teleportPhase === 'gliding') {
      // Aggiorniamo solo lastTickMs per evitare un dt enorme al riprendere.
      internal.walkLastTickMs = now;
      return;
    }
    // WS5 PR5: durante ride (linear vector tra AABB) la camera e' di proprieta'
    // del rideTick. walkTick sospeso (lastTickMs sync per evitare dt enorme
    // al resume post-COMPLETE).
    if (internal.rideState.phase !== 'idle') {
      internal.walkLastTickMs = now;
      return;
    }
    const dt = Math.min(0.1, (now - internal.walkLastTickMs) / 1000);
    internal.walkLastTickMs = now;
    if (dt <= 0) return;
    let cam: import('three').PerspectiveCamera;
    try {
      cam = deps.adapter.getCamera();
    } catch {
      return;
    }
    // XZ movement.
    // Inc C: Shift tenuto → RUN_SPEED (selectWalkSpeed); altrimenti WALK_SPEED.
    const { dx, dz } = computeVelocity(
      internal.walkKeysHeld,
      internal.walkYaw,
      selectWalkSpeed(internal.walkKeysHeld),
      internal.walkTouchInput,
    );
    const newX = cam.position.x + dx * dt;
    const newZ = cam.position.z + dz * dt;
    // Inc C — CROUCH: transizione morbida dell'altezza occhi. 'c' tenuto →
    // CROUCH_EYE_HEIGHT, altrimenti EYE_HEIGHT. prevEye = altezza usata lo
    // scorso tick (coerente con cam.position.y attuale).
    const prevEye = internal.walkState.eyeHeight;
    const targetEye = internal.walkKeysHeld.has('c') ? CROUCH_EYE_HEIGHT : EYE_HEIGHT;
    const curEye = approachEyeHeight(prevEye, targetEye, EYE_HEIGHT_TRANSITION_SPEED, dt);
    if (curEye !== prevEye) {
      // Funnel via dispatch così walkSnapshot/HUD mostrano l'altezza reale.
      dispatchWalkEvent({ type: 'SET_EYE_HEIGHT', eyeHeight: curEye });
    }
    // Y: arco di salto DISACCOPPIATO dall'altezza crouch (Codex). jumpOffset =
    // altezza sopra la posizione "a terra" calcolata con prevEye; gravità
    // sull'offset con floor 0. Quando non accovacciato e a terra → offset 0,
    // comportamento identico a prima.
    const prevFloorEye = internal.walkState.floorHeight + prevEye;
    const jumpOffset = Math.max(0, cam.position.y - prevFloorEye);
    const grav = applyGravity(jumpOffset, internal.walkVY, dt, 0);
    internal.walkVY = grav.vY;
    // Jump (latched: requires keyup + re-press to fire again).
    const onFloorNow = grav.onFloor || grav.y < ON_FLOOR_EPSILON;
    if (internal.walkKeysHeld.has(' ') && !internal.walkJumpHeld && onFloorNow) {
      internal.walkVY = JUMP_VELOCITY;
      internal.walkJumpHeld = true;
    }
    // Camera Y = pavimento + altezza occhi corrente + arco di salto.
    const finalY = internal.walkState.floorHeight + curEye + grav.y;
    // WS5 PR3.2b: vincolo AABB headbox. Se la scena ha multi-viewbox e
    // l'indice array della viewbox corrente e' settato (dispatchViewboxEvent
    // LOAD/SELECT/TELEPORT_START), clampa la posizione finale dell'eye
    // dentro l'AABB headbox con padding di 10cm. Pure function (PR3.1) →
    // costo O(1), nessun side-effect. Scena legacy / pre-spawn → no-op.
    const vbIdx = internal.walkState.currentViewboxArrayIdx;
    let camX = newX;
    let camY = finalY;
    let camZ = newZ;
    if (vbIdx !== null) {
      const activeVb = internal.viewboxState.available[vbIdx];
      if (activeVb !== undefined) {
        const clamped = clampEyeToHeadbox([newX, finalY, newZ], activeVb.headbox);
        camX = clamped[0];
        camY = clamped[1];
        camZ = clamped[2];
        // WS5 PR3.2b (Codex W2): se il clamp Y ha agito (collisione muro
        // superiore/inferiore headbox), azzera walkVY per evitare che la
        // gravita' continui ad accumulare velocita' contro il bordo. Senza
        // questo l'eye resta "incollato" al tetto/pavimento finche' la
        // velocita' non viene drenata da altri tick (jarring UX).
        if (camY !== finalY) {
          internal.walkVY = 0;
        }
      }
    }
    // Apply position + lookAt.
    const fwd = forwardVector(internal.walkYaw, internal.walkPitch);
    try {
      setCameraDispatch(deps.adapter, {
        pos: [camX, camY, camZ],
        lookAt: [camX + fwd[0], camY + fwd[1], camZ + fwd[2]],
        fov: cam.fov,
      });
    } catch (err) {
      console.warn('[walk] setCamera in tick failed:', err);
    }
    // WS5 PR5 — walk-to-edge auto trigger. Chiamato post-setCamera con la
    // velocita' XZ effettiva di questo frame (dx/dz in m/s). Persistenza +
    // cooldown internamente. No-op se ride feature off / scena no-multi-vb.
    edgeDetectionTick({
      now,
      velocityXZ: [dx, dz],
      playerXZ: [camX, camZ],
      camPos: [camX, camY, camZ],
      camYaw: internal.walkYaw,
      camPitch: internal.walkPitch,
    });
  };

  const runWalkEffects = (prev: WalkState, next: WalkState, _event: WalkEvent): void => {
    if (!prev.active && next.active) {
      // Exit other UI states first — camera must be free for FPV control.
      if (internal.tourState.kind !== 'idle') dispatchTourEvent({ type: 'EXIT' });
      if (internal.hotspotsState.openIndex !== -1) dispatchHotspotEvent({ type: 'CLOSE' });
      if (internal.measureState.active) dispatchMeasurementEvent({ type: 'EXIT' });
      // PR5 fix 2026-06-05 (Codex bug1+4): reset ud.occluded di tutti i pin
      // a walk enter. La camera viene snapshottata a default_camera.pos →
      // vecchi raycast hit invalidi.
      for (const pair of internal.ridePinsPairs) {
        const ud = pair.main.userData as PinSpriteUserData;
        ud.occluded = false;
        ud.lastOcclusionCheckMs = 0;
      }
      // WS5 PR4a — Mobile FP path: pointerCoarse + flag ON → joystick +
      // touch controls al posto di pointer-lock + WASD + mouselook. Skip
      // completo della richiesta pointer-lock (Codex Q2: instabile mobile,
      // tentare + fallback graceful aggiungerebbe race senza beneficio).
      // Codex Q11: snapCameraToWalkStart preserva gia' lo yaw corrente.
      if (deps.deviceMatcher.snapshot.pointerCoarse && isMobileFpEnabled()) {
        // Codex iter2 Q10: senza per-frame ticker, dx/dz dal joystick non
        // verrebbero mai applicati → walk inerte. Rollback con walk-error.
        if (!canSubscribeFrames(deps.adapter)) {
          console.warn('[walk] mobile FP unavailable: adapter cannot subscribe frames');
          emit('walk-error', { reason: 'unsupported', embed: deps.embed });
          dispatchWalkEvent({ type: 'EXIT' });
          return;
        }
        abortManualCameraFly();
        if (internal.controls !== null) internal.controls.enabled = false;
        snapCameraToWalkStart();
        // Codex iter2 Q15: rollback se mount touch handlers o frame
        // subscription throw (es. SVG ns unsupported / adapter race).
        try {
          installWalkTouchHandlers();
          internal.walkUnsubFrame = deps.adapter.onFrame((now) => walkTick(now));
        } catch (err) {
          console.warn('[walk] mobile FP setup threw:', err);
          removeWalkTouchHandlers();
          if (internal.walkUnsubFrame !== null) {
            internal.walkUnsubFrame();
            internal.walkUnsubFrame = null;
          }
          if (internal.controls !== null) internal.controls.enabled = true;
          emit('walk-error', { reason: 'unsupported', embed: deps.embed });
          dispatchWalkEvent({ type: 'EXIT' });
          return;
        }
        internal.walkInputMode = 'touch';
        return;
      }
      // Pointer-lock acquisition: defensive against unsupported APIs.
      // 4.M: emit 'walk-error' alongside the EXIT so the UI can show
      // a contextual toast (reason discriminator + embed flag).
      if (typeof deps.canvas.requestPointerLock !== 'function') {
        console.warn('[walk] pointer lock API not available; aborting');
        emit('walk-error', { reason: 'unsupported', embed: deps.embed });
        dispatchWalkEvent({ type: 'EXIT' });
        return;
      }
      // Inc A hardening (Codex): Walk prende la camera → ferma un fly preset
      // manuale in volo PRIMA di disabilitare i controls.
      abortManualCameraFly();
      if (internal.controls !== null) internal.controls.enabled = false;
      snapCameraToWalkStart();
      installWalkInputHandlers();
      deps.canvas.style.cursor = 'none';
      // Subscribe per-frame ticker (piggybacks on the same RAF the FPS
      // monitor already uses; cleanup in removeWalkFrameSub).
      if (canSubscribeFrames(deps.adapter)) {
        internal.walkUnsubFrame = deps.adapter.onFrame((now) => walkTick(now));
      }
      internal.walkInputMode = 'keyboard';
      // Bump the sequence so an in-flight Promise.reject from a previous
      // entry can be discriminated (Codex iter 1 code MINOR fix).
      internal.walkPointerLockSeq += 1;
      const seq = internal.walkPointerLockSeq;
      let lockResult: unknown;
      try {
        lockResult = deps.canvas.requestPointerLock();
      } catch (err) {
        console.warn('[walk] pointer lock threw synchronously:', err);
        emit('walk-error', { reason: 'pointer-lock-failed', embed: deps.embed });
        dispatchWalkEvent({ type: 'EXIT' });
        return;
      }
      if (
        lockResult !== undefined &&
        lockResult !== null &&
        typeof (lockResult as Promise<unknown>).then === 'function'
      ) {
        (lockResult as Promise<unknown>).catch((err) => {
          // Stale reject guard: only roll back if THIS request is still
          // the latest one AND walk is currently active. Otherwise an
          // entry → exit → re-entry race would let the first reject
          // deactivate the new session — and emit a stale toast (Codex
          // iter 3 plan addition #1: walk-error stays under the same
          // sequence guard).
          if (internal.walkPointerLockSeq !== seq) return;
          if (!internal.walkState.active) return;
          console.warn('[walk] pointer lock denied:', err);
          emit('walk-error', { reason: 'pointer-lock-failed', embed: deps.embed });
          dispatchWalkEvent({ type: 'EXIT' });
        });
      }
    }
    if (prev.active && !next.active) {
      if (
        typeof document.exitPointerLock === 'function' &&
        document.pointerLockElement === deps.canvas
      ) {
        document.exitPointerLock();
      }
      if (internal.walkUnsubFrame !== null) {
        internal.walkUnsubFrame();
        internal.walkUnsubFrame = null;
      }
      removeWalkInputHandlers();
      // WS5 PR4a — cleanup mobile FP (no-op se non installato).
      removeWalkTouchHandlers();
      internal.walkInputMode = null;
      deps.canvas.style.cursor = '';
      // Codex iter 1 code MAJOR fix: realign controls.target to the
      // current camera lookAt BEFORE re-enabling. OrbitControls.update()
      // reads the stored target and would otherwise snap the camera
      // back toward the pre-walk view on the very next frame.
      if (internal.controls !== null) {
        try {
          const cam = deps.adapter.getCamera();
          const fwd = forwardVector(internal.walkYaw, internal.walkPitch);
          internal.controls.target.set(
            cam.position.x + fwd[0],
            cam.position.y + fwd[1],
            cam.position.z + fwd[2],
          );
        } catch (err) {
          console.warn('[walk] failed to realign controls.target on exit:', err);
        }
        internal.controls.enabled = true;
      }
    }
  };

  const dispatchWalkEvent = (event: WalkEvent): void => {
    if (internal.disposed) return;
    const prev = internal.walkState;
    const next = walkTransition(prev, event);
    if (next === prev) return;
    internal.walkState = next;
    runWalkEffects(prev, next, event);
    // Codex iter 2 plan MAJOR fix: runWalkEffects may dispatch a nested
    // EXIT (e.g. pointer-lock unsupported / sync throw → rollback). The
    // nested dispatch already updated internal.walkState + emitted its
    // own snapshot. We MUST NOT emit `next` again here, or the renderer
    // would see a stale active=true after the actual rollback.
    if (internal.walkState !== next) return;
    emit('walk-state', walkSnapshot(next));
  };

  // ============================================================
  // WS5 PR3.2a — Viewbox state dispatch + walk sync
  // ============================================================
  //
  // dispatchViewboxEvent funnels ogni mutazione viewbox via viewboxTransition,
  // poi sincronizza walkState.currentViewboxArrayIdx via dispatchWalkEvent.
  // Single-source-of-truth: ViewboxState e' authoritative; walkState ha solo
  // la slice del numerico array_idx per il consumer walkTick (PR3.2b).
  // No 'runViewboxEffects' separato in PR3.2a (effects come flyToCamera per
  // teleport, AABB clamp camera read, ecc. arrivano in PR3.2b/PR3.3).
  const dispatchViewboxEvent = (event: ViewboxEvent): void => {
    if (internal.disposed) return;
    const prev = internal.viewboxState;
    const next = viewboxTransition(prev, event);
    if (next === prev) return;
    internal.viewboxState = next;
    // Sync walkState slice se l'array idx e' cambiato. Allineato a pattern
    // dispatchWalkEvent: dispatch nested per coerenza single-funnel walk.
    if (next.currentArrayIdx !== prev.currentArrayIdx) {
      // PR5 fix 2026-06-05 (Codex bug1+4): reset ud.occluded di tutti i pin
      // al cambio viewbox. Camera teleport → vecchi raycast hit invalidi.
      for (const pair of internal.ridePinsPairs) {
        const ud = pair.main.userData as PinSpriteUserData;
        ud.occluded = false;
        ud.lastOcclusionCheckMs = 0;
      }
      dispatchWalkEvent({ type: 'SET_VIEWBOX', arrayIdx: next.currentArrayIdx });
      // 2026-06-05 (sub-task 1): floor scene-specific. Priorita':
      //  1. vb.floor_y esplicito (coord_version>=2 dal worker finalize)
      //  2. fallback legacy default_camera.pos[1] - EYE_HEIGHT (parser v1)
      // Pre-fix FLOOR_HEIGHT=0 hardcoded produceva camera spawn a Y=1.65
      // clampata al top dell'headbox AABB = "cammino sul soffitto".
      if (next.currentArrayIdx !== null) {
        const activeVb = next.available[next.currentArrayIdx];
        if (activeVb !== undefined) {
          let nextFloor: number | null = null;
          if (Number.isFinite(activeVb.floor_y)) {
            nextFloor = activeVb.floor_y as number;
          } else {
            const headY = activeVb.default_camera.pos[1];
            const eyeH = Number.isFinite(activeVb.eye_height_m)
              ? (activeVb.eye_height_m as number)
              : EYE_HEIGHT;
            if (Number.isFinite(headY)) nextFloor = headY - eyeH;
          }
          if (nextFloor !== null) {
            dispatchWalkEvent({
              type: 'SET_FLOOR_HEIGHT',
              floorHeight: nextFloor,
            });
          }
        }
      }
    }
    emit('viewbox-state', viewboxSnapshot(next));
  };

  // ============================================================
  // WS5 PR5 — Ride-mode + pin (ride between AABB headboxes)
  // ============================================================
  //
  // Razionale: il GS ONIRI ha qualita' anisotropa (calibrata DENTRO AABB).
  // Tra AABB il rendering scende → ride accelerated linear vector + look-
  // guided maschera la zona di bassa qualita'. Pattern pin Three.Sprite
  // + walk-to-edge auto trigger. Layer puro src/ride/*; orchestrator wiring
  // qui sotto.

  const dispatchRideEvent = (event: RideEvent): void => {
    if (internal.disposed) return;
    const prev = internal.rideState;
    const next = rideTransition(prev, event);
    if (next === prev) return;
    internal.rideState = next;
    // Phase transitions handled by tick (pos+lookAt advance) + completion
    // dispatch. Qui solo emit snapshot per HUD/telemetry e gestione effetti
    // collaterali del trigger START_TO_VIEWBOX (mutex hotspot/tour/teleport).
    if (event.type === 'START_TO_VIEWBOX' || event.type === 'START_TO_HOTSPOT') {
      // Mutex: chiudi hotspot aperto, esci tour, cancella click-to-move.
      if (internal.hotspotsState.openIndex !== -1) {
        dispatchHotspotEvent({ type: 'CLOSE' });
      }
      if (internal.tourState.kind !== 'idle') {
        dispatchTourEvent({ type: 'EXIT' });
      }
      internal.clickToMoveAbort?.abort();
      // Reset walk input buffers (jump, keys held). Walk yaw/pitch saranno
      // settati a target dal COMPLETE per coerenza con walk-aware teleport.
      if (internal.walkState.active) {
        internal.walkVY = 0;
        internal.walkJumpHeld = false;
        internal.walkKeysHeld.clear();
        internal.walkTouchInput = null;
      }
      // Disabilita orbit controls durante ride (orchestrator owns camera).
      if (internal.controls !== null) internal.controls.enabled = false;
    }
    if (event.type === 'COMPLETE') {
      // Sync walk yaw/pitch al target (analogo walk-aware teleport PR4 sub1.2).
      // Aggiorna currentArrayIdx se ride_to_viewbox.
      if (prev.phase === 'ride_to_viewbox' && prev.targetVbArrayIdx >= 0) {
        // Update viewbox state-machine: SELECT (instantaneous) al target idx.
        // selectViewbox (teleport) avrebbe spawnato un flyToCamera duplicato,
        // qui usiamo SELECT diretto (no glide, e' gia' al target).
        dispatchViewboxEvent({ type: 'SELECT', arrayIdx: prev.targetVbArrayIdx });
      }
      if (internal.walkState.active) {
        internal.walkYaw = prev.targetYaw;
        internal.walkPitch = clampPitch(prev.targetPitch);
        internal.walkVY = 0;
        internal.walkJumpHeld = false;
        internal.walkKeysHeld.clear();
        internal.walkTouchInput = null;
        internal.walkLastTickMs = performance.now();
      }
      // Reset edge tracking (no auto-trigger immediato).
      internal.rideEdgeTracking.outwardSince = null;
      // PR5 fix 2026-06-05 (Codex bug1+4): reset ud.occluded di tutti i pin
      // al ride COMPLETE. Camera position e' cambiata → vecchi raycast hit
      // non sono piu' validi. Lascia il tick successivo ricomputarli da zero.
      for (const pair of internal.ridePinsPairs) {
        const ud = pair.main.userData as PinSpriteUserData;
        ud.occluded = false;
        ud.lastOcclusionCheckMs = 0;
      }
      // Riabilita orbit controls se nessun altro owner.
      if (
        internal.controls !== null &&
        internal.tourState.kind === 'idle' &&
        !internal.walkState.active &&
        !internal.cinematicActive
      ) {
        internal.controls.enabled = true;
      }
    }
    if (event.type === 'ABORT') {
      // Same cleanup di COMPLETE ma SENZA SELECT viewbox + senza sync walk
      // (la camera potrebbe essere a meta' ride → caller gestisce snap).
      internal.rideEdgeTracking.outwardSince = null;
    }
    emit('ride-state', rideSnapshot(next, performance.now()));
  };

  // Setup pin layer per la scena corrente. Chiamato post-LOAD viewboxes.
  // Asincrono (texture loading). Tollerante a unload concorrente.
  const setupRidePinsAsync = async (
    viewboxes: readonly import('../api/types.js').ModelViewbox[],
  ): Promise<void> => {
    if (!internal.rideFeatureEnabled) return;
    if (internal.disposed) return;
    if (viewboxes.length < 2) return; // <2 viewbox → no pin (ride non ha senso)
    let pinTexture: THREE_PR5.Texture;
    try {
      pinTexture = await loadPinTexture('/');
    } catch (err) {
      console.warn('[ride] pin texture load failed, ride disabled:', err);
      return;
    }
    if (internal.disposed) return;
    if (internal.ridePinsLayer !== null) return; // race-guard
    let scene: THREE_PR5.Scene;
    try {
      scene = deps.adapter.getThreeScene();
    } catch (err) {
      console.warn('[ride] getThreeScene unavailable, ride disabled:', err);
      return;
    }
    const layer = new THREE_PR5.Group();
    layer.name = 'oniri-viewbox-pins';
    const pairs: PinSpritePair[] = [];
    const defaultEye = EYE_HEIGHT;
    for (let i = 0; i < viewboxes.length; i++) {
      const vb = viewboxes[i];
      if (vb === undefined) continue;
      try {
        const pair = createPinSprite(vb, i, pinTexture, defaultEye);
        layer.add(pair.halo);
        layer.add(pair.main);
        pairs.push(pair);
      } catch (err) {
        console.warn('[ride] createPinSprite failed for arrayIdx', i, err);
      }
    }
    scene.add(layer);
    internal.ridePinsLayer = layer;
    internal.ridePinsPairs = pairs;
  };

  // Cleanup pin layer (su detachLoadedFeatures). Pure cleanup.
  const teardownRidePins = (): void => {
    if (internal.ridePinsLayer === null) return;
    try {
      const scene = deps.adapter.getThreeScene();
      scene.remove(internal.ridePinsLayer);
    } catch {
      /* adapter già teardown — best effort */
    }
    for (const pair of internal.ridePinsPairs) {
      pair.main.material.dispose();
      pair.halo.material.dispose();
    }
    internal.ridePinsLayer = null;
    internal.ridePinsPairs = [];
  };

  // Tick principale ride: advance pos+yaw+pitch, dispatch COMPLETE su end.
  const rideTick = (now: number): void => {
    if (internal.disposed) return;
    if (internal.rideState.phase === 'idle') return;
    const st = internal.rideState;
    const transform = computeRideTransform({
      startPos: st.startPos,
      startYaw: st.startYaw,
      startPitch: st.startPitch,
      targetPos: st.targetPos,
      targetYaw: st.targetYaw,
      targetPitch: st.targetPitch,
      startTimeMs: st.startTimeMs,
      durationMs: st.durationMs,
      nowMs: now,
    });
    try {
      const fwd = forwardVector(transform.yaw, clampPitch(transform.pitch));
      // PR3: fov omesso → helper legge fov corrente via getCameraState(),
      // preservando semantica legacy `setCamera(pos, lookAt)` (no-change fov).
      setCameraDispatch(deps.adapter, {
        pos: [transform.pos[0], transform.pos[1], transform.pos[2]],
        lookAt: [
          transform.pos[0] + fwd[0],
          transform.pos[1] + fwd[1],
          transform.pos[2] + fwd[2],
        ],
      });
    } catch (err) {
      console.warn('[ride] setCamera failed mid-tick, abort:', err);
      dispatchRideEvent({ type: 'ABORT', nowMs: now });
      return;
    }
    if (isRideDone(st, now)) {
      dispatchRideEvent({ type: 'COMPLETE', nowMs: now });
    }
  };

  // Visibility update + occlusion check (10Hz throttled per-pin). Tick ogni
  // frame ma il lavoro pesante (raycast) e' throttled.
  const pinVisibilityUpdate = (now: number): void => {
    if (!internal.rideFeatureEnabled) return;
    if (internal.ridePinsLayer === null) return;
    if (internal.ridePinsPairs.length === 0) return;
    let camera: THREE_PR5.PerspectiveCamera;
    try {
      camera = deps.adapter.getCamera();
    } catch {
      return;
    }
    const viewport = {
      width: deps.canvas.clientWidth,
      height: deps.canvas.clientHeight,
    };
    const mobile = deps.deviceMatcher.snapshot.pointerCoarse;
    const currentArrayIdx = internal.viewboxState.currentArrayIdx ?? -1;
    // Build mobile visible set se mobile.
    let mobileVisibleSet: Set<number> | null = null;
    if (mobile) {
      const positions: { vbArrayIdx: number; world: import('../api/types.js').Vec3 }[] = [];
      for (const pair of internal.ridePinsPairs) {
        const ud = pair.main.userData as PinSpriteUserData;
        if (ud.vbArrayIdx === currentArrayIdx) continue;
        positions.push({
          vbArrayIdx: ud.vbArrayIdx,
          world: [pair.main.position.x, pair.main.position.y, pair.main.position.z],
        });
      }
      mobileVisibleSet = buildMobileVisibleSet(positions, camera, viewport);
    }
    // Per ogni pin pair: compute visibility + apply.
    for (const pair of internal.ridePinsPairs) {
      const ud = pair.main.userData as PinSpriteUserData;
      const camPos = camera.position;
      const dx = pair.main.position.x - camPos.x;
      const dy = pair.main.position.y - camPos.y;
      const dz = pair.main.position.z - camPos.z;
      const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const decision = computePinVisibility({
        vbArrayIdx: ud.vbArrayIdx,
        currentArrayIdx,
        distanceCamPin: distance,
        occluded: ud.occluded,
        mobileVisibleSet,
        lastOcclusionCheckMs: ud.lastOcclusionCheckMs,
        nowMs: now,
      });
      pair.main.visible = decision.visible;
      pair.halo.visible = decision.visible;
      pair.main.material.opacity = decision.opacity;
      pair.halo.material.opacity = decision.opacity * 0.6;
      ud.clickable = decision.clickable;
      // Occlusion check throttled (10Hz). PR5 fix 2026-06-05 (Codex bug1+4):
      // RIMOSSO il guard `decision.visible` che bloccava la rivalutazione
      // — una volta marcato occluded, decision.visible=false → if mai
      // eseguito → ud.occluded restava true per sempre → pin nascosto
      // sticky anche dopo aver liberato la linea di vista. Ora il raycast
      // viene rieseguito sempre (throttled 10Hz) per pin not-current e
      // not-mobile-capped, garantendo recovery quando il pin torna visibile.
      // Skip se pin e' current viewbox (gia' hidden by-rule) o fuori
      // mobile-cap, per evitare raycast inutili.
      // PR5 fix 2026-06-06 (post-utente bug walk no-pin): salta del tutto
      // l'occlusion check se rideOcclusionEnabled=false (default OFF). Senza
      // mesh collider reali, raycast contro SplatMesh nasconde i pin in
      // walk first-person dietro muri splat → UX peggiore (pin invisibili
      // a portata di click). Pin Unity-style sempre visibili attraverso
      // muri = default UX. Opt-in via `?ride_occlusion=1` per scene future
      // con mesh collider o headbox-per-stanza dove l'occlusion ha senso.
      if (
        internal.rideOcclusionEnabled &&
        decision.shouldCheckOcclusion &&
        ud.vbArrayIdx !== currentArrayIdx &&
        (mobileVisibleSet === null || mobileVisibleSet.has(ud.vbArrayIdx)) &&
        canRaycast(deps.adapter)
      ) {
        ud.lastOcclusionCheckMs = now;
        try {
          const v = new THREE_PR5.Vector3(
            pair.main.position.x,
            pair.main.position.y,
            pair.main.position.z,
          );
          v.project(camera);
          // Skip se pin e' fuori viewport (frustum culling): hit raycast
          // sarebbe falso positivo (out-of-frustum splat). Mantieni
          // ud.occluded invariato (verra' rivalutato al prossimo tick
          // quando pin rientra in viewport).
          if (v.x < -1 || v.x > 1 || v.y < -1 || v.y > 1 || v.z > 1) continue;
          const sx = ((v.x + 1) / 2) * viewport.width;
          const sy = ((1 - v.y) / 2) * viewport.height;
          const hit = raycastDispatch(deps.adapter, sx, sy);
          if (hit !== null) {
            const hitDist = Math.sqrt(
              (hit[0] - camPos.x) * (hit[0] - camPos.x) +
                (hit[1] - camPos.y) * (hit[1] - camPos.y) +
                (hit[2] - camPos.z) * (hit[2] - camPos.z),
            );
            // EPS contro splat pavimento immediato (0.3m).
            ud.occluded = hitDist < distance - 0.3;
          } else {
            ud.occluded = false;
          }
        } catch {
          /* raycast best-effort */
        }
      }
    }
  };

  // Walk-to-edge auto trigger detection (chiamato dentro walkTick post-clamp).
  // Pure side: dispatch RIDE start se thresholds + persistence + cooldown OK.
  const edgeDetectionTick = (args: {
    now: number;
    velocityXZ: readonly [number, number];
    playerXZ: readonly [number, number];
    camPos: readonly [number, number, number];
    camYaw: number;
    camPitch: number;
  }): void => {
    if (!internal.rideFeatureEnabled) return;
    if (internal.rideState.phase !== 'idle') return;
    if (isInCooldown(internal.rideState, args.now)) return;
    if (!internal.walkState.active) return;
    const vbState = internal.viewboxState;
    if (vbState.currentArrayIdx === null) return;
    const currentVb = vbState.available[vbState.currentArrayIdx];
    if (currentVb === undefined) return;
    if (vbState.available.length < 2) return;
    const outwardNow = isEdgeOutwardFrame({
      velocityXZ: args.velocityXZ,
      playerXZ: args.playerXZ,
      headbox: currentVb.headbox,
    });
    const triggered = trackEdgePersistence(internal.rideEdgeTracking, outwardNow, args.now);
    if (!triggered) return;
    const candidateIdx = electCandidateViewbox({
      velocityXZ: args.velocityXZ,
      playerXZ: args.playerXZ,
      viewboxes: vbState.available,
      currentArrayIdx: vbState.currentArrayIdx,
    });
    if (candidateIdx === null) return;
    startRideToViewbox(
      candidateIdx,
      'edge_auto',
      args.now,
      args.camPos,
      args.camYaw,
      args.camPitch,
    );
  };

  // Helper unificato per startare ride_to_viewbox. Calcola target pos =
  // headbox.center con Y = pin height (= floor + eye + offset) per evitare
  // landing al pavimento. lookAt anchor = headbox.center (centroide stanza).
  const startRideToViewbox = (
    arrayIdx: number,
    trigger: RideTrigger,
    now: number,
    startPos: readonly [number, number, number],
    startYaw: number,
    startPitch: number,
  ): void => {
    if (!internal.rideFeatureEnabled) return;
    if (internal.rideState.phase !== 'idle') return;
    if (internal.cinematicActive) return;
    if (internal.xrState.status === 'starting' || internal.xrState.status === 'in-session') {
      return;
    }
    if (internal.viewboxState.teleportPhase === 'gliding') return;
    const vb = internal.viewboxState.available[arrayIdx];
    if (vb === undefined) return;
    // PR5 fix 2026-06-05: target = default_camera.pos (spawn point unico per
    // viewbox) invece di headbox.center. Roma 4vb headbox FULL-SCENE condiviso
    // → headbox.center identico per tutti i vb → tutti i ride finivano nello
    // stesso punto. default_camera.pos e' calibrato Seurat per ogni viewpoint.
    const targetPos: import('../api/types.js').Vec3 = [
      vb.default_camera.pos[0],
      vb.default_camera.pos[1],
      vb.default_camera.pos[2],
    ];
    // Anchor lookAt: stesso punto target → player guarda dove sta andando.
    const targetAnchor: import('../api/types.js').Vec3 = [
      vb.default_camera.pos[0],
      vb.default_camera.pos[1],
      vb.default_camera.pos[2],
    ];
    // PR5 fix 2026-06-06 (post-utente feedback): ride PURE TRANSLATION,
    // NO ROTATION. Pre-fix targetYaw=vb.default_camera.yaw faceva ruotare
    // la camera durante il ride (look-guided del PLAN sealed). UX feedback:
    // l'utente vuole solo traslazione lungo il vettore, senza rotazione →
    // mantengo lo yaw/pitch attuali della camera invariati durante il ride.
    // L'utente arriva nella nuova stanza guardando nella stessa direzione
    // in cui era prima. Se vuole orientarsi diversamente, usa il mouse.
    const targetYaw = startYaw;
    const targetPitch = startPitch;
    const dist = distance3([startPos[0], startPos[1], startPos[2]], targetPos);
    const durationMs = computeRideDuration(dist);
    // reduced-motion → snap istantaneo (set camera direct, dispatch COMPLETE).
    if (deps.deviceMatcher.snapshot.reducedMotion) {
      try {
        const fwd = forwardVector(targetYaw, clampPitch(targetPitch));
        // PR3: fov omesso → helper legge fov corrente (no-change semantica).
        setCameraDispatch(deps.adapter, {
          pos: [targetPos[0], targetPos[1], targetPos[2]],
          lookAt: [
            targetPos[0] + fwd[0],
            targetPos[1] + fwd[1],
            targetPos[2] + fwd[2],
          ],
        });
      } catch (err) {
        console.warn('[ride] reduced-motion snap setCamera failed:', err);
        return;
      }
      // SELECT viewbox + sync walk (idempotente con COMPLETE flow).
      dispatchViewboxEvent({ type: 'SELECT', arrayIdx });
      if (internal.walkState.active) {
        internal.walkYaw = targetYaw;
        internal.walkPitch = clampPitch(targetPitch);
        internal.walkVY = 0;
        internal.walkLastTickMs = performance.now();
      }
      // Arma cooldown via dispatch ABORT (idempotente, no SELECT duplicato).
      // dispatch RIDE START_TO_VIEWBOX seguito da COMPLETE non e' usato qui:
      // teniamo state idle + cooldown armato manualmente.
      internal.rideState = {
        ...INITIAL_RIDE_STATE,
        cooldownUntilMs: now + 1500,
      };
      internal.rideEdgeTracking.outwardSince = null;
      emit('ride-state', rideSnapshot(internal.rideState, now));
      return;
    }
    dispatchRideEvent({
      type: 'START_TO_VIEWBOX',
      arrayIdx,
      startPos: [startPos[0], startPos[1], startPos[2]],
      startYaw,
      startPitch,
      targetPos,
      targetAnchor,
      targetYaw,
      targetPitch,
      durationMs,
      startTimeMs: now,
      trigger,
    });
  };

  // Click handler globale pin (pointerup canvas). Raycast contro ridePinsLayer.
  // Hit clickable → dispatch RIDE start. Pattern: solo cursor visibile
  // (no pointerlock); su mobile sempre attivo. Tappato per BREAKDOWN OUTPUT
  // priority sopra click-to-move (= se pin hit, click-to-move NON parte).
  const installRidePinClickHandler = (): void => {
    if (internal.ridePinClickHandler !== null) return;
    let downX = 0;
    let downY = 0;
    let downValid = false;
    const onDown = (ev: PointerEvent): void => {
      if (ev.button !== 0) {
        downValid = false;
        return;
      }
      downX = ev.clientX;
      downY = ev.clientY;
      downValid = true;
    };
    const onUp = (ev: PointerEvent): void => {
      if (!downValid) return;
      downValid = false;
      if (!internal.rideFeatureEnabled) return;
      if (internal.ridePinsLayer === null) return;
      if (internal.rideState.phase !== 'idle') return;
      if (internal.cinematicActive) return;
      if (internal.xrState.status === 'starting' || internal.xrState.status === 'in-session') {
        return;
      }
      if (internal.viewboxState.teleportPhase === 'gliding') return;
      // Drag-vs-click guard (5px).
      if (Math.hypot(ev.clientX - downX, ev.clientY - downY) >= 5) return;
      let camera: THREE_PR5.PerspectiveCamera;
      try {
        camera = deps.adapter.getCamera();
      } catch {
        return;
      }
      // PR5 fix 2026-06-06 (post-utente bug click no-ride): durante pointer
      // lock (walk mode FP desktop) il cursore e' nascosto al centro
      // viewport; l'utente "punta" col centro vista. Il pre-fix skippava
      // il click se pointer-lock attivo → click pin in walk mai funzionante.
      // Ora se pointer-lock attivo, raycast dal centro (NDC 0,0) per
      // simulare crosshair-aim. Senza pointer-lock, raycast dalle coord
      // del click come standard.
      const pointerLockActive =
        typeof document !== 'undefined' && document.pointerLockElement === deps.canvas;
      const rect = deps.canvas.getBoundingClientRect();
      const ndc = new THREE_PR5.Vector2(
        pointerLockActive ? 0 : ((ev.clientX - rect.left) / rect.width) * 2 - 1,
        pointerLockActive ? 0 : -((ev.clientY - rect.top) / rect.height) * 2 + 1,
      );
      const raycaster = new THREE_PR5.Raycaster();
      raycaster.setFromCamera(ndc, camera);
      const hits = raycaster.intersectObjects(internal.ridePinsLayer.children, false);
      for (const hit of hits) {
        const ud = hit.object.userData as PinSpriteUserData;
        if (ud.kind !== 'viewbox-pin') continue;
        if (!ud.clickable) continue;
        if (!hit.object.visible) continue;
        // Hit valido → start ride. Stop propagation per evitare click-to-move.
        ev.stopPropagation();
        ev.preventDefault();
        const camPos = camera.position;
        const now = performance.now();
        // Yaw/pitch da camera attuale (walk mode usa walkYaw/Pitch, altrimenti
        // derivati da camera.rotation — usiamo walkYaw/Pitch se walk attivo,
        // altrimenti fallback lookAtYawPitch dal target).
        let startYaw: number;
        let startPitch: number;
        if (internal.walkState.active) {
          startYaw = internal.walkYaw;
          startPitch = internal.walkPitch;
        } else {
          // Derivazione da camera quaternion: forward = camera.getWorldDirection.
          // Approssimazione: usiamo lookAtYawPitch verso il target del pin.
          const pinPos: import('../api/types.js').Vec3 = [
            hit.object.position.x,
            hit.object.position.y,
            hit.object.position.z,
          ];
          const yp = lookAtYawPitch([camPos.x, camPos.y, camPos.z], pinPos);
          startYaw = yp.yaw;
          startPitch = yp.pitch;
        }
        startRideToViewbox(
          ud.vbArrayIdx,
          'pin',
          now,
          [camPos.x, camPos.y, camPos.z],
          startYaw,
          startPitch,
        );
        return;
      }
    };
    deps.canvas.addEventListener('pointerdown', onDown);
    deps.canvas.addEventListener('pointerup', onUp);
    internal.ridePinClickHandler = onUp;
    // Stash onDown ref as a property on the function to detach later.
    (onUp as unknown as { __ride_onDown?: typeof onDown }).__ride_onDown = onDown;
  };

  const removeRidePinClickHandler = (): void => {
    if (internal.ridePinClickHandler === null) return;
    const onUp = internal.ridePinClickHandler;
    const onDown = (onUp as unknown as { __ride_onDown?: (ev: PointerEvent) => void })
      .__ride_onDown;
    deps.canvas.removeEventListener('pointerup', onUp);
    if (onDown !== undefined) {
      deps.canvas.removeEventListener('pointerdown', onDown);
    }
    internal.ridePinClickHandler = null;
  };

  // ============================================================
  // 4.K WebXR (VR / AR) wiring
  // ============================================================
  //
  // dispatchXREvent funnels every state mutation through xrTransition;
  // runXREffects schedules side effects (detection async, exit other
  // modes, adapter.enterXR, adapter.exitXR). The orchestrator owns
  // a `xrSessionSeq` guard so a stale adapter.enterXR(mode) Promise
  // .catch — e.g. resolved after teardown / exitXR-while-starting —
  // is swallowed instead of re-emitting an ERROR snapshot post
  // cleanup (Codex iter 4 plan MAJOR #1 fix).

  const runXREffects = (prev: XRState, next: XRState, _event: XREvent): void => {
    // Inc A hardening (Codex): quando XR LASCIA la proprietà della camera
    // (starting/in-session → altro stato: SESSION_ENDED/ERROR), riabilita gli
    // OrbitControls SOLO se nessun altro owner li possiede (es. erano rimasti
    // disabilitati da un fly preset manuale abortito all'ingresso XR).
    const xrWasOwner = prev.status === 'starting' || prev.status === 'in-session';
    const xrIsOwner = next.status === 'starting' || next.status === 'in-session';
    if (
      xrWasOwner &&
      !xrIsOwner &&
      internal.controls !== null &&
      internal.tourState.kind === 'idle' &&
      internal.tourAbort === null &&
      internal.hotspotAbort === null &&
      !internal.walkState.active &&
      // WS5 PR3.3c (Codex C1): NON riabilitare controls se cinematic e' ancora
      // owner. Il finally di runCinematicTourInOrchestrator gestira'.
      !internal.cinematicActive
    ) {
      internal.controls.enabled = true;
    }

    // OPEN_MODAL transition: kick off async detection. Capture the
    // currently-focused element so we can restore focus on close.
    if (!prev.modalOpen && next.modalOpen && prev.status !== 'error') {
      const active = (
        typeof document !== 'undefined' ? document.activeElement : null
      ) as HTMLElement | null;
      internal.xrModalOpenerEl = active;
      if (!canEnterXR(deps.adapter)) {
        dispatchXREvent({ type: 'ERROR', message: 'Adapter senza supporto WebXR' });
        return;
      }
      internal.xrSessionSeq += 1;
      const seq = internal.xrSessionSeq;
      void detectXRSupport()
        .then((support) => {
          if (internal.disposed) return;
          if (internal.xrSessionSeq !== seq) return; // stale
          dispatchXREvent({ type: 'DETECT_RESULT', support });
        })
        .catch((err: unknown) => {
          if (internal.disposed) return;
          if (internal.xrSessionSeq !== seq) return;
          const msg = err instanceof Error ? err.message : 'Detection fallita';
          dispatchXREvent({ type: 'ERROR', message: msg });
        });
    }

    // CLOSE_MODAL: restore focus to opener (best-effort; opener may
    // have been removed from DOM in the meantime).
    if (prev.modalOpen && !next.modalOpen && next.status !== 'in-session') {
      const opener = internal.xrModalOpenerEl;
      internal.xrModalOpenerEl = null;
      if (opener !== null) {
        try {
          opener.focus();
        } catch {
          // ignore stale element
        }
      }
    }

    // SESSION_REQUEST: exit other modes first, then call adapter.
    if (prev.status !== 'starting' && next.status === 'starting' && next.activeMode !== null) {
      if (internal.tourState.kind !== 'idle') dispatchTourEvent({ type: 'EXIT' });
      if (internal.hotspotsState.openIndex !== -1) dispatchHotspotEvent({ type: 'CLOSE' });
      if (internal.measureState.active) dispatchMeasurementEvent({ type: 'EXIT' });
      if (internal.walkState.active) dispatchWalkEvent({ type: 'EXIT' });
      // WS5 PR5: XR takeover → abort ride in volo (XR owna camera).
      if (internal.rideState.phase !== 'idle') {
        dispatchRideEvent({ type: 'ABORT', nowMs: performance.now() });
      }
      // Inc A: XR è un takeover completo → esci da click-to-move (rimuove
      // handler/cursor + aborta il glide in volo).
      exitClickToMoveIfActive();
      // WS5 PR3.3c (Codex C1+iter2 W3b): XR session takeover → abort SIA
      // sequencer SIA landing fly. abortCinematicAll garantisce che il
      // land non procede sotto XR owner. Il finally del sequencer emette
      // cinematic-state false e ripristina coerenza UI.
      abortCinematicAll();

      if (!canEnterXR(deps.adapter)) {
        dispatchXREvent({ type: 'ERROR', message: 'Adapter senza supporto WebXR' });
        return;
      }
      // Inc A hardening (Codex): XR prende la camera → ferma un fly preset
      // manuale in volo (tourState idle + tourAbort attivo).
      abortManualCameraFly();
      internal.xrSessionSeq += 1;
      const seq = internal.xrSessionSeq;
      const mode = next.activeMode;
      void deps.adapter.enterXR(mode).catch((err: unknown) => {
        if (internal.disposed) return;
        if (internal.xrSessionSeq !== seq) return;
        const msg = err instanceof Error ? err.message : 'Avvio sessione fallito';
        dispatchXREvent({ type: 'ERROR', message: msg });
      });
      // SESSION_STARTED dispatch arrives via adapter 'xr-session-started'
      // listener installed in attachLoadedFeatures.
    }
  };

  const dispatchXREvent = (event: XREvent): void => {
    if (internal.disposed) return;
    const prev = internal.xrState;
    const next = xrTransition(prev, event);
    if (next === prev) return;
    internal.xrState = next;
    runXREffects(prev, next, event);
    // Same guard as dispatchWalkEvent (Codex iter 2 plan MAJOR #2 fix
    // for walk → reused here): runXREffects can dispatch a nested
    // event (e.g. ERROR after capability check fails synchronously).
    // The nested dispatch already updated internal.xrState + emitted
    // its snapshot. We MUST NOT emit `next` again or the consumer
    // sees a stale snapshot.
    if (internal.xrState !== next) return;
    emit('xr-state', xrSnapshot(next));
  };

  const attachLoadedFeatures = (): void => {
    if (internal.disposed) return;
    // 4.L: if the URL carries a shared camera, decode it and apply as initial
    // view (skip auto-fit). Falls through to auto-fit on invalid encoding.
    const state = deps.store.getState();
    const route = state.kind === 'loaded' ? state.route : null;
    if (route?.cameraEncoded != null && route.cameraEncoded.length > 0) {
      const decoded = decodeCameraState(route.cameraEncoded);
      if (decoded !== null) {
        try {
          internal.initialCamera = decoded;
          setCameraDispatch(deps.adapter, {
            pos: decoded.pos,
            lookAt: decoded.lookAt,
            fov: decoded.fov,
          });
          if (internal.controls !== null) {
            internal.controls.target.set(decoded.lookAt[0], decoded.lookAt[1], decoded.lookAt[2]);
            internal.controls.update();
          }
        } catch (err) {
          console.warn('[orchestrator] shared camera apply failed:', err);
          internal.initialCamera = null;
        }
      } else {
        console.warn('[orchestrator] cameraEncoded invalid, falling back to auto-fit');
      }
    }
    // 4.E: compute auto-fit camera and apply as initial view (also reset target)
    // ONLY if no shared camera was decoded above.
    if (internal.initialCamera === null && canFitCamera(deps.adapter)) {
      try {
        const fit = deps.adapter.computeFitCamera();
        if (fit !== null) {
          internal.initialCamera = fit;
          setCameraDispatch(deps.adapter, { pos: fit.pos, lookAt: fit.lookAt, fov: fit.fov });
          if (internal.controls !== null) {
            internal.controls.target.set(fit.lookAt[0], fit.lookAt[1], fit.lookAt[2]);
            internal.controls.update();
          }
        }
      } catch (err) {
        console.warn('[orchestrator] auto-fit failed (non-fatal):', err);
      }
    }

    // 4.F: sortedPresets is pre-cached in loadScene() before SCENE_LOADED so
    // the renderer's first Loaded mount sees presets immediately. We don't
    // re-sort here.

    // Step 7 fix: hotspotsState.visible is now initialized in loadScene()
    // BEFORE dispatch(SCENE_LOADED) — same race-fix pattern as floorplan.
    // The flip here AFTER dispatch left the renderer mounted with
    // visible=false and never re-emitted hotspot-state.

    // 4.H: floorplanState.visible is pre-set in loadScene() before
    // SCENE_LOADED so the renderer's first Loaded mount sees it
    // immediately. Even with degenerate calibration the minimap is
    // mounted (the ticker below skips position emission in that case).

    // 4.E + 4.F + 4.G: FPS monitor + tour ticker + hotspot positions share
    // the per-frame subscription.
    if (canSubscribeFrames(deps.adapter)) {
      const monitor = createFpsMonitor();
      let frameCounter = 0;
      const unsubFrame = deps.adapter.onFrame((now) => {
        monitor.tick(now);
        // Throttle fps-update emission to ~10 hz (every 6 frames).
        frameCounter += 1;
        if (frameCounter % 6 === 0) {
          emit('fps-update', { fps: monitor.getCurrentFps() });
        }
        // 4.F: drive tour state machine.
        dispatchTourEvent({ type: 'TICK', now });
        // WS5 PR5: drive ride tick + pin visibility update. rideTick
        // sospende walkTick durante linear vector; pinVisibilityUpdate
        // applica fade/occlusion rules + mobile cap.
        rideTick(now);
        pinVisibilityUpdate(now);
        // 4.G: emit hotspot screen positions only when the UI needs them.
        // Cleanup contract: skip the entire .map() and the emit when not
        // visible OR no hotspots — no allocations, no listener noise.
        if (internal.hotspotsState.visible && internal.sortedHotspots.length > 0) {
          const viewport = {
            width: deps.canvas.clientWidth,
            height: deps.canvas.clientHeight,
          };
          const positions: HotspotScreenPosition[] = internal.sortedHotspots.map((h, idx) => {
            const proj = projectWorldToScreenDispatch(deps.adapter, h.pos, viewport);
            // Inc C: distance-fade dell'opacità del dot.
            return {
              idx,
              x: proj.x,
              y: proj.y,
              visible: proj.visible,
              opacity: fadeOpacity(proj.distance),
            };
          });
          emit('hotspot-positions', { positions });
        }
        // 4.J: emit measurement projections (per-point screen coords +
        // cumulative segment distances) throttled to ~30 hz. Cleanup
        // contract: skip when measure mode is off OR no points yet —
        // no allocations, no listener noise.
        if (
          internal.measureState.active &&
          internal.measureState.points.length > 0 &&
          now - internal.lastMeasureProjEmit >= 33
        ) {
          internal.lastMeasureProjEmit = now;
          try {
            const viewport = {
              width: deps.canvas.clientWidth,
              height: deps.canvas.clientHeight,
            };
            const projections: MeasurementProjection[] = internal.measureState.points.map(
              (p, idx) => {
                const proj = projectWorldToScreenDispatch(deps.adapter, p.world, viewport);
                return {
                  idx,
                  screenX: proj.x,
                  screenY: proj.y,
                  visible: proj.visible,
                };
              },
            );
            const distances: number[] = [];
            for (let i = 0; i < internal.measureState.points.length - 1; i++) {
              const a = internal.measureState.points[i];
              const b = internal.measureState.points[i + 1];
              if (a !== undefined && b !== undefined) {
                const dx = b.world[0] - a.world[0];
                const dy = b.world[1] - a.world[1];
                const dz = b.world[2] - a.world[2];
                distances.push(Math.sqrt(dx * dx + dy * dy + dz * dz));
              }
            }
            emit('measurement-projections', { projections, distances });
          } catch (err) {
            console.warn('[measure] projections emit skipped:', err);
          }
        }
        // 4.H: emit floor plan position throttled to ~30 hz. Cleanup
        // contract: skip when no floorplan, hidden, or degenerate
        // calibration (UI keeps dot/cone hidden in those states).
        if (
          internal.floorplan !== null &&
          internal.floorplanState.visible &&
          !internal.floorplanState.degenerateCalibration &&
          now - internal.lastFloorplanEmit >= 33
        ) {
          internal.lastFloorplanEmit = now;
          try {
            const cur = currentCamera();
            const [px, py] = worldToPixel([cur.pos[0], cur.pos[2]], internal.floorplan.calibration);
            const heading = cameraHeadingDeg(cur.pos, cur.lookAt);
            emit('floorplan-position', { pxX: px, pxY: py, headingDeg: heading });
          } catch (err) {
            console.warn('[floorplan] position emit skipped:', err);
          }
        }
      });
      const unsubWarn = monitor.onWarning(() => {
        emit('fps-warning', { fps: monitor.getCurrentFps() });
      });
      internal.loadedTeardown.push(unsubFrame, unsubWarn);
    }

    // 4.K: adapter XR session lifecycle events. SESSION_STARTED is
    // gated by xrSessionSeq (a stale start triggered by an old click
    // would arrive here after teardown — drop it). SESSION_ENDED is
    // unconditional because the end signal can come from outside our
    // control (HMD power-off, headset removed) and must always settle
    // the SM.
    if (canEnterXR(deps.adapter) && canExitXR(deps.adapter)) {
      const onXRStarted = (ev: Event): void => {
        const detail = (ev as CustomEvent<{ mode: 'vr' | 'ar' }>).detail;
        dispatchXREvent({ type: 'SESSION_STARTED', mode: detail.mode });
      };
      const onXREnded = (): void => {
        dispatchXREvent({ type: 'SESSION_ENDED' });
      };
      deps.adapter.events.addEventListener('xr-session-started', onXRStarted);
      deps.adapter.events.addEventListener('xr-session-ended', onXREnded);
      internal.xrAdapterUnsubStarted = () =>
        deps.adapter.events.removeEventListener('xr-session-started', onXRStarted);
      internal.xrAdapterUnsubEnded = () =>
        deps.adapter.events.removeEventListener('xr-session-ended', onXREnded);
      internal.loadedTeardown.push(internal.xrAdapterUnsubStarted, internal.xrAdapterUnsubEnded);
    }

    // 4.E + 4.L + 4.F: keyboard shortcuts (only active in loaded state).
    // Source of truth for keys is src/lib/shortcuts.ts SHORTCUTS.
    // Inc C: in walk mode la tastiera serve al movimento (WASD/Space/Shift/'c'
    // crouch). Sopprimi i comandi-tastiera della toolbar (incluso 'c'=share)
    // mentre cammini, restituendo false (non consumato → niente preventDefault).
    // 'v' (toggle walk) ed Escape NON sono soppressi.
    const walkSuppress =
      (fn: () => unknown): (() => unknown) =>
      () =>
        internal.walkState.active ? false : fn();
    const unbindKeys = bindShortcuts(window, {
      [SHORTCUTS.fullscreen]: walkSuppress(() => {
        void api.toggleFullscreen();
      }),
      // Step 7 #3 fix: screenshot shortcut removed — collided with
      // WASD walk movement (S = backward). Now only via toolbar button.
      [SHORTCUTS.reset]: walkSuppress(() => api.resetView()),
      [SHORTCUTS.perf]: walkSuppress(() => api.togglePerfHud()),
      [SHORTCUTS.share]: walkSuppress(() => {
        void api.shareUrl();
      }),
      [SHORTCUTS.tour]: walkSuppress(() => {
        // 4.F: 't' toggles tour. From idle → start; from any active state →
        // exit. Pause is reachable via the pill button only (keyboard
        // semantics stay simple).
        if (internal.tourState.kind === 'idle') api.startTour();
        else api.exitTour();
      }),
      [SHORTCUTS.hotspots]: walkSuppress(() => api.toggleHotspots()),
      [SHORTCUTS.floorplan]: walkSuppress(() => api.toggleFloorplan()),
      // Step 7 #3 fix: measure shortcut removed — D collided with WASD
      // walk right-strafe. Now only via toolbar button.
      [SHORTCUTS.walk]: () => {
        // WS5 PR4a — Mobile FP attivo: lo shortcut V e' di fatto inerte
        // su touch (no tastiera), ma se un BT keyboard mobile lo emette
        // permettiamo toggleWalk se il flag mobile-fp e' on. Se off,
        // restiamo sul vecchio toast educational (kill switch Codex Q7).
        if (deps.deviceMatcher.snapshot.pointerCoarse && !isMobileFpEnabled()) {
          emit('walk-error', { reason: 'touch', embed: deps.embed });
          return;
        }
        api.toggleWalk();
      },
      [SHORTCUTS.xr]: walkSuppress(() => api.toggleXR()),
      Backspace: () => {
        // 4.J: explicit return false when not consuming so browser
        // defaults (back navigation, history input focus) survive when
        // measure mode is inactive or there are no points to remove.
        if (!internal.measureState.active || internal.measureState.points.length === 0) {
          return false;
        }
        api.removeLastMeasurePoint();
        return true;
      },
      Escape: () => {
        // 4.F + 4.G + 4.J + 4.I1 + 4.K priority:
        //   tour > hotspot popup > measure > walk > xr modal > fullscreen.
        // Tour wins because exiting it frees the camera the user expects
        // to control; xr modal lives below walk so the user can dismiss
        // FPV first if both are stacked. xr in-session does NOT consume
        // ESC (browser/headset owns the exit ergonomics).
        if (internal.tourState.kind !== 'idle') {
          api.exitTour();
          return true;
        }
        if (internal.hotspotsState.openIndex !== -1) {
          api.closeHotspot();
          return true;
        }
        if (internal.measureState.active) {
          api.exitMeasure();
          return true;
        }
        if (internal.walkState.active) {
          api.exitWalk();
          return true;
        }
        // 4.K Codex iter 1 plan MAJOR #3 fix: ESC during status='starting'
        // is consumed (return true) but does NOT dispatch CLOSE_MODAL —
        // the reducer would no-op anyway, but consuming prevents
        // accidental fall-through to fullscreen exit while the session
        // is still being granted.
        if (internal.xrState.modalOpen) {
          if (internal.xrState.status === 'starting') return true;
          api.closeXRModal();
          return true;
        }
        if (isFullscreen()) {
          void exitFullscreen();
          return true;
        }
        return false;
      },
    });
    internal.loadedTeardown.push(unbindKeys);

    // Step 7 — Bug J fix: keep WebGL backbuffer + camera aspect in sync
    // with the canvas CSS size. Without this, entering/exiting fullscreen
    // (or window resize) leaves renderer.setSize + camera.aspect at the
    // values computed in adapter.init() — Three.js stretches the stale
    // backbuffer over the new viewport, distorting the scene horizontally.
    // ResizeObserver covers fullscreenchange + resize + DPR change in a
    // single subscription. setSize(_, _, false) preserves CSS sizing
    // (the canvas keeps width:100vw;height:100vh from main.ts).
    // PR4 — resize dispatch engine-neutral. DPR strategy A (Codex INDIPENDENT):
    // resizeDispatch usa adapter.getViewport().pixelRatio corrente (capped at
    // init), preservando il fill-rate governor. Spark adapter.resize() copre
    // renderer.setSize + composer.setSize + camera.aspect/updateProjectionMatrix
    // in un'unica chiamata; il fallback legacy ricostruisce il loop pre-PR4.
    // canSetPostFx + resizePostFx restano OUT del bridge (call site separato):
    // serve quando l'adapter NON ha capability engineNeutralCamera (fake adapter
    // test che pure dichiarino postFx — caso teorico).
    const ro = new ResizeObserver(() => {
      const w = deps.canvas.clientWidth;
      const h = deps.canvas.clientHeight;
      if (w === 0 || h === 0) return;
      resizeDispatch(deps.adapter, { width: w, height: h });
      // PR4 — postFx resize: solo legacy path (engine-neutral coperto da
      // Spark resize internamente via composer.setSize). Idempotent no-op
      // se composer unbuilt.
      if (!canUseEngineNeutralCamera(deps.adapter) && canSetPostFx(deps.adapter)) {
        deps.adapter.resizePostFx(w, h);
      }
    });
    ro.observe(deps.canvas);
    internal.loadedTeardown.push(() => ro.disconnect());

    // Inc D: seed the effective post-FX state once the adapter is ready (the
    // composer is lazy, so this only builds it if the session preference was ON).
    applyPostFxState();

    // WS5 PR5: setup ride pin layer + click handler. Asincrono (texture
    // load); tollerante a unload concorrente. Skip se ride feature off
    // o se scena ha <2 viewbox.
    if (internal.rideFeatureEnabled) {
      const viewboxes = internal.viewboxState.available;
      if (viewboxes.length >= 2) {
        void setupRidePinsAsync(viewboxes);
        installRidePinClickHandler();
        internal.loadedTeardown.push(() => {
          teardownRidePins();
          removeRidePinClickHandler();
          internal.rideState = INITIAL_RIDE_STATE;
          internal.rideEdgeTracking.outwardSince = null;
        });
      }
    }
  };

  const teardownLoadedFeatures = (): void => {
    // 4.F: stop any in-flight tour animation and reset state before unhooking
    // the per-frame subscription (which would otherwise drop ticker events).
    internal.tourAbort?.abort();
    internal.tourAbort = null;
    internal.tourState = { kind: 'idle' };
    internal.sortedPresets = [];
    internal.lastSnapshotAt = 0;
    // 4.G: same teardown discipline for hotspots — abort fly-to before the
    // ticker is removed so finally() callbacks don't reactivate controls
    // mid-cleanup.
    internal.hotspotAbort?.abort();
    internal.hotspotAbort = null;
    internal.hotspotsState = { openIndex: -1, visible: false };
    internal.sortedHotspots = [];
    // 4.H: floor plan minimap — no fly to abort, just clear cached state.
    internal.floorplanState = { visible: false, imageError: false, degenerateCalibration: false };
    internal.floorplan = null;
    internal.lastFloorplanEmit = 0;
    // 4.J: detach measure click handler + reset state. removeMeasureClickHandler
    // is a no-op if not currently installed.
    removeMeasureClickHandler();
    // Inc A: teardown click-to-move (no-op se non installato) + abort glide.
    removeClickToMoveHandler();
    internal.clickToMoveActive = false;
    internal.measureState = { active: false, points: [], units: 'm' };
    internal.lastMeasureProjEmit = 0;
    internal.measurePointSeq = 0;
    // 4.I1: walk mode teardown — exit pointer-lock, detach listeners,
    // unsubscribe RAF ticker, reset cursor + state.
    if (internal.walkState.active) {
      if (
        typeof document.exitPointerLock === 'function' &&
        document.pointerLockElement === deps.canvas
      ) {
        document.exitPointerLock();
      }
    }
    if (internal.walkUnsubFrame !== null) {
      internal.walkUnsubFrame();
      internal.walkUnsubFrame = null;
    }
    removeWalkInputHandlers();
    // WS5 PR4a — teardown mobile FP: cleanup joystick + touch-controls.
    // Codex CRIT #5: necessario qui (oltre che in runWalkEffects EXIT) per
    // coprire dispose con walk attivo (route swap mid-walk lasciava DOM stale).
    removeWalkTouchHandlers();
    internal.walkInputMode = null;
    deps.canvas.style.cursor = '';
    internal.walkState = {
      active: false,
      eyeHeight: EYE_HEIGHT,
      floorHeight: FLOOR_HEIGHT,
      currentViewboxArrayIdx: null,
    };
    internal.walkYaw = 0;
    internal.walkPitch = 0;
    internal.walkVY = 0;
    internal.walkLastTickMs = 0;
    internal.walkJumpHeld = false;
    // WS5 PR3.2a (Codex W1): teardown e' chiamato in dispose() oggi; future
    // route/model swap (PR3.2b+) deve trovare viewboxState a INITIAL per
    // evitare stale viewbox post-teardown. Set diretto OK (no emit: dispose
    // sta gia' chiudendo events).
    internal.viewboxState = INITIAL_VIEWBOX_STATE;
    // 4.K Codex iter 3 plan MAJOR fix: when 'starting' or 'in-session'
    // we must call api.exitXR() to either cancel the pending start
    // (cancel guard via xrSessionSeq + adapter.xrStartSeq) or end the
    // live session. Fire-and-forget — teardown can't await. The
    // wasStarting branch in api.exitXR ensures the SM transitions
    // back to idle even though no 'xr-session-ended' adapter event
    // will fire (session never reached setSession).
    if (internal.xrState.status === 'starting' || internal.xrState.status === 'in-session') {
      void api.exitXR();
    }
    // Bump the seq so any in-flight detection or enterXR catch is
    // invalidated (the .catch checks against the captured seq).
    internal.xrSessionSeq += 1;
    internal.xrState = INITIAL_XR_STATE;
    internal.xrModalOpenerEl = null;
    internal.xrAdapterUnsubStarted = null;
    internal.xrAdapterUnsubEnded = null;
    while (internal.loadedTeardown.length > 0) {
      const fn = internal.loadedTeardown.pop();
      if (fn !== undefined) {
        try {
          fn();
        } catch (err) {
          console.warn('[orchestrator] loaded teardown threw:', err);
        }
      }
    }
    // WS5 PR3.3c: abort cinematic tour + landing se attivi + reset state.
    // Subito DOPO loadedTeardown cosi' eventuali RAF/timer pendenti del
    // sequencer + del fly di landing vengono cancellati prima del prossimo
    // mount (Codex iter2 W3b: abort sia loop sia land).
    if (internal.cinematicAbort !== null || internal.cinematicLandAbort !== null) {
      abortCinematicAll();
      internal.cinematicAbort = null;
      internal.cinematicLandAbort = null;
    }
    if (internal.cinematicActive) {
      internal.cinematicActive = false;
      emit('cinematic-state', { active: false });
    }
  };

  // WS5 PR3.3c (Codex iter2 W3b): aborta SIA il loop sequencer SIA il
  // landing in volo. Usato da tutti i takeover camera (SESSION_REQUEST XR,
  // showOverview, applyPreset, startTour, teardownLoadedFeatures).
  const abortCinematicAll = (): void => {
    internal.cinematicAbort?.abort();
    internal.cinematicLandAbort?.abort();
  };

  // WS5 PR3.3c: sequencer cinematic-tour. Wrappa runCinematicTour passando
  // flyToCamera bound su adapter+controls + scheduler globale. Setta i
  // controls disabled durante il tour + abort dei mutex (tour preset, hotspot,
  // click-to-move, viewbox glide). Emit 'cinematic-state' a start e a end.
  const runCinematicTourInOrchestrator = async (): Promise<void> => {
    if (internal.cinematicActive) return; // guard race
    const N = internal.viewboxState.available.length;
    if (N < 2) return;

    // Mutex: abort le animazioni concorrenti.
    internal.tourAbort?.abort();
    internal.tourAbort = null;
    internal.hotspotAbort?.abort();
    internal.hotspotAbort = null;
    internal.clickToMoveAbort?.abort();
    internal.clickToMoveAbort = null;
    // Codex iter5: chiudi popup hotspot se aperto, cosi' nessun residuo
    // state lascia next/prev click triggerare fly mid-cinematic (ora gated
    // ma cleanup preventivo allinea UI).
    if (internal.hotspotsState.openIndex !== -1) {
      dispatchHotspotEvent({ type: 'CLOSE' });
    }

    if (internal.controls !== null) internal.controls.enabled = false;

    const ctrl = new AbortController();
    const landCtrl = new AbortController();
    internal.cinematicAbort = ctrl;
    internal.cinematicLandAbort = landCtrl;
    internal.cinematicActive = true;
    emit('cinematic-state', { active: true });

    const flyTo = (
      from: CameraTarget,
      to: CameraTarget,
      durationMs: number,
      signal: AbortSignal,
    ): Promise<void> =>
      flyToCamera(deps.adapter, internal.controls, from, to, {
        durationMs,
        signal,
      });

    const startCam = currentCamera();
    const defaultArrayIdx =
      internal.viewboxState.currentArrayIdx !== null ? internal.viewboxState.currentArrayIdx : 0;
    const reducedMotion = deps.deviceMatcher.snapshot.reducedMotion;

    try {
      await runCinematicTour(
        { flyTo },
        {
          viewboxes: internal.viewboxState.available,
          defaultArrayIdx,
          startCamera: startCam,
          reducedMotion,
          signal: ctrl.signal,
          // Codex W3a: passa la camera reale al landing → no snap dal target
          // logico precedente al moment dell'abort.
          getCurrentCamera: () => currentCamera(),
          // Codex iter2 W3b: cinematicLandAbort dedicato per il landing fly,
          // cancellabile da tutti i takeover camera (XR session, overview,
          // applyPreset, startTour, teardown). Senza signal dedicato, un
          // takeover che arriva DURANTE il landing aborta solo il tour
          // gia' finito, lasciando il fly di land procedere a fronte di un
          // owner camera diverso (race).
          landSignal: landCtrl.signal,
        },
      );
    } catch (err) {
      if (!isAbortError(err)) {
        console.warn('[orchestrator] cinematic tour failed:', err);
      }
    } finally {
      // Token-gate cleanup: solo se siamo ancora owner (no takeover XR/walk).
      if (internal.cinematicAbort === ctrl) {
        internal.cinematicAbort = null;
      }
      if (internal.cinematicLandAbort === landCtrl) {
        internal.cinematicLandAbort = null;
      }
      internal.cinematicActive = false;
      emit('cinematic-state', { active: false });
      // WS5 PR3.3c (Codex iter3): re-enable controls SOLO se nessun altro
      // takeover camera attivo. Include tourAbort + hotspotAbort
      // (showOverview/applyPreset abortano cinematicLandAbort poi avviano
      // flyManualTo → il NOSTRO finally non deve riabilitare controls
      // mentre il loro fly e' in volo). Pattern allineato a runXREffects.
      if (
        !internal.walkState.active &&
        internal.tourState.kind === 'idle' &&
        internal.tourAbort === null &&
        internal.hotspotAbort === null &&
        internal.xrState.status !== 'starting' &&
        internal.xrState.status !== 'in-session' &&
        internal.controls !== null
      ) {
        internal.controls.enabled = true;
      }
    }
  };

  const api: Orchestrator = {
    events,
    async start(): Promise<void> {
      // ...assigned below
      throw new Error('start not yet bound');
    },
    async dispose(): Promise<void> {
      throw new Error('dispose not yet bound');
    },
    async takeScreenshot(): Promise<void> {
      if (!canCaptureFrame(deps.adapter)) {
        console.warn('[orchestrator] screenshot capability not available');
        return;
      }
      try {
        const frame = await deps.adapter.captureFrame();
        const filename = defaultScreenshotFilename(internal.lastFilename ?? 'oniri-viewer');
        downloadBlob(filename, frame.blob);
        // Revoke after a short delay so the download anchor has time to read it.
        setTimeout(() => frame.revoke(), 1000);
      } catch (err) {
        console.warn('[orchestrator] screenshot failed:', err);
      }
    },
    resetView(): void {
      // 4.F: leaving a running tour first keeps the SM in sync with what the
      // user sees (camera snaps to initialCamera). Otherwise the tour would
      // keep ticking against a stale camera baseline.
      if (internal.tourState.kind !== 'idle') {
        dispatchTourEvent({ type: 'EXIT' });
      }
      const fit = internal.initialCamera;
      if (fit === null) {
        console.warn('[orchestrator] no initial camera saved (resetView no-op)');
        return;
      }
      try {
        setCameraDispatch(deps.adapter, { pos: fit.pos, lookAt: fit.lookAt, fov: fit.fov });
        if (internal.controls !== null) {
          internal.controls.target.set(fit.lookAt[0], fit.lookAt[1], fit.lookAt[2]);
          internal.controls.update();
        }
      } catch (err) {
        console.warn('[orchestrator] resetView failed:', err);
      }
    },
    async showOverview(): Promise<void> {
      // Inc B: dollhouse/overview glide. No-op se disposto o se XR possiede
      // la camera; richiede la capability cameraFit + un bbox valido.
      if (internal.disposed) return;
      if (internal.xrState.status === 'starting' || internal.xrState.status === 'in-session') {
        return;
      }
      if (!canComputeOverviewCamera(deps.adapter)) return;
      const overview = deps.adapter.computeOverviewCamera();
      if (overview === null) return; // bbox vuoto / splat non pronto → no-op
      // Takeover camera: esci dagli altri modi (come resetView/applyPreset/walk).
      if (internal.tourState.kind !== 'idle') dispatchTourEvent({ type: 'EXIT' });
      if (internal.hotspotsState.openIndex !== -1) dispatchHotspotEvent({ type: 'CLOSE' });
      if (internal.measureState.active) dispatchMeasurementEvent({ type: 'EXIT' });
      if (internal.walkState.active) dispatchWalkEvent({ type: 'EXIT' });
      exitClickToMoveIfActive();
      // WS5 PR3.3c (Codex C1+iter2 W3b): overview takeover → abort sequencer
      // + landing fly (entrambi possono essere in volo).
      abortCinematicAll();
      await flyManualTo(overview, { warnLabel: 'showOverview' });
    },
    togglePostFx(): void {
      // Inc D: flip the user preference. Unavailable on coarse pointer (mobile
      // MVP) — the toolbar shows the button disabled, but guard here too.
      if (internal.disposed || deps.deviceMatcher.snapshot.pointerCoarse) return;
      internal.postFxUserPreference = !internal.postFxUserPreference;
      writePostFxPreference(internal.postFxUserPreference, safeSessionStorage());
      applyPostFxState();
    },
    getPostFxSnapshot(): PostFxSnapshot {
      return resolvePostFxSnapshot({
        userPreference: internal.postFxUserPreference,
        tourActive: internal.tourState.kind !== 'idle',
        pointerCoarse: deps.deviceMatcher.snapshot.pointerCoarse,
      });
    },
    async toggleFullscreen(): Promise<void> {
      try {
        if (isFullscreen()) {
          await exitFullscreen();
        } else {
          // Fullscreen target must contain BOTH layers (canvas + ui) so the
          // toolbar/perf HUD/hints stay visible. Walk up to the nearest
          // ancestor that includes the UI root sibling, falling back to
          // documentElement when the layout is unexpected.
          const canvasLayer = deps.canvas.parentElement;
          const target = canvasLayer?.parentElement ?? canvasLayer ?? document.documentElement;
          await requestFullscreen(target);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'unknown';
        emit('fullscreen-error', { message });
      }
    },
    togglePerfHud(): void {
      internal.perfHudVisible = !internal.perfHudVisible;
      emit('perf-toggle', { visible: internal.perfHudVisible });
    },
    async applyPreset(presetId: string): Promise<void> {
      const idx = internal.sortedPresets.findIndex((p) => p.id === presetId);
      if (idx < 0) {
        console.warn('[orchestrator] applyPreset: unknown preset id', presetId);
        return;
      }
      // WS5 PR5: ride attiva → abort takeover (preset preference > ride).
      // L'utente intenzionalmente sceglie un preset = vuole quel target.
      if (internal.rideState.phase !== 'idle') {
        dispatchRideEvent({ type: 'ABORT', nowMs: performance.now() });
      }
      // WS5 PR3.3c (Codex C1+iter2 W3b): applyPreset takeover → abort
      // sequencer + landing fly.
      abortCinematicAll();
      // 4.G: a preset shifts the camera to a destination unrelated to the
      // hotspot popup; close it so the popup doesn't anchor to a stale view.
      if (internal.hotspotsState.openIndex !== -1) {
        dispatchHotspotEvent({ type: 'CLOSE' });
      }
      // 4.F: tour-aware. While a tour is running, hand off to the SM via
      // GOTO so the dispatch unifies effect scheduling and UI snapshots.
      if (internal.tourState.kind !== 'idle') {
        dispatchTourEvent({ type: 'GOTO', index: idx, now: performance.now() });
        return;
      }
      // Manual idle path: glide condiviso (flyManualTo) + emit 'preset-applied'
      // solo se restiamo owner del token (Codex Inc B: estratto da applyPreset).
      const target = internal.sortedPresets[idx] as Preset;
      await flyManualTo(presetCamera(target), {
        warnLabel: 'applyPreset',
        onComplete: () => emit('preset-applied', { presetId: target.id }),
      });
    },
    startTour(): void {
      // Inc A: mutex bidirezionale — il tour prende il controllo della camera.
      exitClickToMoveIfActive();
      cancelHotspotPlacementIfActive(); // F8 reverse mutex
      // WS5 PR3.3c (Codex C1+iter2 W3b): tour preset takeover → abort
      // sequencer + landing fly (entrambi possono essere in volo).
      abortCinematicAll();
      dispatchTourEvent({ type: 'START', now: performance.now() });
    },
    pauseTour(): void {
      dispatchTourEvent({ type: 'PAUSE', now: performance.now() });
    },
    resumeTour(): void {
      dispatchTourEvent({ type: 'RESUME', now: performance.now() });
    },
    exitTour(): void {
      dispatchTourEvent({ type: 'EXIT' });
    },
    nextTourStep(): void {
      const cur = currentTourIndex();
      if (cur < 0 || cur + 1 >= internal.sortedPresets.length) return;
      dispatchTourEvent({ type: 'GOTO', index: cur + 1, now: performance.now() });
    },
    prevTourStep(): void {
      const cur = currentTourIndex();
      if (cur <= 0) return;
      dispatchTourEvent({ type: 'GOTO', index: cur - 1, now: performance.now() });
    },
    getSortedPresets(): readonly Preset[] {
      return internal.sortedPresets;
    },
    getTourSnapshot(): TourSnapshot {
      return tourSnapshot(internal.tourState, internal.sortedPresets, performance.now());
    },
    openHotspot(id: string): void {
      // WS5 PR3.3c (Codex iter4): cinematic owna la camera; openHotspot
      // avvierebbe un flyToCamera concorrente al fly cinematic. Gating
      // mutex + il finally hotspot avrebbe inoltre riabilitato i controls
      // mid-cinematic (race ownership). UI (hotspot dot/strip) NON gating-
      // gated, ma il click utente in pratica e' raro durante un tour da
      // 22.5s. Mutex orchestrator-level e' il fix corretto.
      if (internal.cinematicActive) return;
      const idx = internal.sortedHotspots.findIndex((h) => h.id === id);
      if (idx < 0) {
        console.warn('[orchestrator] openHotspot: unknown hotspot id', id);
        return;
      }
      dispatchHotspotEvent({ type: 'OPEN', index: idx });
    },
    closeHotspot(): void {
      dispatchHotspotEvent({ type: 'CLOSE' });
    },
    nextHotspot(): void {
      // WS5 PR3.3c (Codex iter5): mutex cinematic (NEXT triggera flyTo nel
      // run effect). closeHotspot resta permesso (utente puo' voler
      // chiudere il popup).
      if (internal.cinematicActive) return;
      dispatchHotspotEvent({ type: 'NEXT' });
    },
    prevHotspot(): void {
      if (internal.cinematicActive) return;
      dispatchHotspotEvent({ type: 'PREV' });
    },
    toggleHotspots(): void {
      if (internal.cinematicActive) return;
      dispatchHotspotEvent({ type: 'TOGGLE_VISIBILITY' });
    },
    getSortedHotspots(): readonly Hotspot[] {
      return internal.sortedHotspots;
    },
    getHotspotsSnapshot(): HotspotsSnapshot {
      return hotspotsSnapshot(internal.hotspotsState, internal.sortedHotspots);
    },
    toggleFloorplan(): void {
      dispatchFloorplanEvent({ type: 'TOGGLE_VISIBILITY' });
    },
    closeFloorplan(): void {
      dispatchFloorplanEvent({ type: 'CLOSE' });
    },
    reportFloorplanImageError(): void {
      dispatchFloorplanEvent({ type: 'IMAGE_ERROR' });
    },
    teleportOnFloorplan(worldXZ: readonly [number, number]): void {
      teleportOnFloorplan(worldXZ);
    },
    teleportOnFloorplanPixel(pixelXY: readonly [number, number]): void {
      if (internal.floorplan === null) return;
      const worldXZ = pixelToWorld(pixelXY, internal.floorplan.calibration);
      teleportOnFloorplan(worldXZ);
    },
    toggleMeasure(): void {
      // WS5 PR3.3c mutex: cinematic tour gating.
      if (internal.cinematicActive) return;
      // Step 7 mutex: walk and measure use overlapping click semantics
      // (walk: WASD movement + look; measure: click two points). Active
      // simultaneously is broken UX. If we're about to ENTER measure
      // mode, exit walk first.
      if (!internal.measureState.active && internal.walkState.active) {
        dispatchWalkEvent({ type: 'EXIT' });
      }
      if (!internal.measureState.active) {
        exitClickToMoveIfActive();
        cancelHotspotPlacementIfActive(); // F8 reverse mutex
      }
      dispatchMeasurementEvent({ type: 'TOGGLE_ACTIVE' });
    },
    toggleClickToMove(): void {
      // WS5 PR3.3c mutex: cinematic tour gating.
      if (internal.cinematicActive) return;
      if (internal.clickToMoveActive) {
        exitClickToMoveIfActive();
        return;
      }
      // Entrata: mutex con walk + measure + tour (click/camera sovrapposti).
      if (internal.walkState.active) dispatchWalkEvent({ type: 'EXIT' });
      if (internal.measureState.active) dispatchMeasurementEvent({ type: 'EXIT' });
      if (internal.tourState.kind !== 'idle') dispatchTourEvent({ type: 'EXIT' });
      cancelHotspotPlacementIfActive(); // F8 reverse mutex
      internal.clickToMoveActive = true;
      installClickToMoveHandler();
    },
    getClickToMoveActive(): boolean {
      return internal.clickToMoveActive;
    },
    beginHotspotPlacement(
      onPick: (pos: readonly [number, number, number] | null) => void,
      onCancelled: () => void,
    ): () => void {
      if (internal.disposed) {
        onCancelled();
        return () => {};
      }
      // Re-arm idempotente: annulla un eventuale placement precedente.
      cancelHotspotPlacementIfActive();
      // Forward mutex: il placement possiede il click sul canvas → esci dai
      // modi conflittuali (come fa toggleClickToMove).
      if (internal.walkState.active) dispatchWalkEvent({ type: 'EXIT' });
      if (internal.measureState.active) dispatchMeasurementEvent({ type: 'EXIT' });
      if (internal.tourState.kind !== 'idle') dispatchTourEvent({ type: 'EXIT' });
      exitClickToMoveIfActive();
      installHotspotPlacementHandler(onPick, onCancelled);
      return cancelHotspotPlacementIfActive;
    },
    cancelHotspotPlacement(): void {
      cancelHotspotPlacementIfActive();
    },
    exitMeasure(): void {
      dispatchMeasurementEvent({ type: 'EXIT' });
    },
    addMeasurePoint(screenX: number, screenY: number): void {
      if (!internal.measureState.active) return;
      const adapter = deps.adapter;
      if (!canRaycast(adapter)) {
        console.warn('[measure] raycast capability unavailable — click ignored');
        return;
      }
      // Step 7 #5 fix: snap to existing measure point when click lands
      // within SNAP_PX of any already-placed point's screen projection
      // (Trimble / Forge / Polycam pattern). Lets the user close chains
      // and re-anchor segments without sub-pixel precision.
      const SNAP_PX = 12;
      try {
        const viewport = {
          width: deps.canvas.clientWidth,
          height: deps.canvas.clientHeight,
        };
        for (const existing of internal.measureState.points) {
          const proj = projectWorldToScreenDispatch(deps.adapter, existing.world, viewport);
          if (!proj.visible) continue;
          const dx = proj.x - screenX;
          const dy = proj.y - screenY;
          if (Math.hypot(dx, dy) <= SNAP_PX) {
            // Reuse the existing world coords (visually identical, makes
            // chains terminate exactly on the start point).
            internal.measurePointSeq += 1;
            dispatchMeasurementEvent({
              type: 'ADD_POINT',
              id: `m_${internal.measurePointSeq}`,
              world: [...existing.world] as [number, number, number],
            });
            return;
          }
        }
      } catch (err) {
        // If projection fails (camera unavailable etc.), fall through to
        // a regular raycast — snap is best-effort.
        console.warn('[measure] snap projection skipped:', err);
      }

      // PR4 — raycastDispatch dispatch a raycastHit engine-neutral o raycast
      // legacy; canRaycast guard preserva il check capability per fake adapter.
      let hit: Vec3 | null;
      try {
        hit = raycastDispatch(adapter, screenX, screenY);
      } catch (err) {
        console.warn('[measure] raycast threw:', err);
        return;
      }
      if (hit === null) return;
      internal.measurePointSeq += 1;
      const id = `m_${internal.measurePointSeq}`;
      dispatchMeasurementEvent({
        type: 'ADD_POINT',
        id,
        world: [hit[0], hit[1], hit[2]],
      });
    },
    removeLastMeasurePoint(): void {
      dispatchMeasurementEvent({ type: 'REMOVE_LAST' });
    },
    resetMeasurements(): void {
      dispatchMeasurementEvent({ type: 'RESET_ALL' });
    },
    setMeasurementUnits(units: MeasurementUnits): void {
      dispatchMeasurementEvent({ type: 'SET_UNITS', units });
    },
    getMeasurementsSnapshot(): MeasurementsSnapshot {
      return measurementsSnapshot(internal.measureState);
    },
    getRaycastAvailable(): boolean {
      return canRaycast(deps.adapter);
    },
    toggleWalk(): void {
      // WS5 PR3.3c mutex: cinematic tour owna la camera → walk no-op finche'
      // il tour non viene abortito. L'utente puo' uscire dal tour cliccando
      // di nuovo il button cinematic (toggleCinematicTour abort).
      if (internal.cinematicActive) return;
      // WS5 PR4a (Codex CRIT #2): gate firstPersonAvailable lato orchestrator,
      // non solo nel toolbar. Senza viewboxes la camera non ha AABB headbox
      // e walk-error mostrerebbe un toast generico; meglio no-op silenzioso
      // (l'UI mostra il button shown-disabled).
      if (!internal.walkState.active && !internal.viewboxState.firstPersonAvailable) {
        return;
      }
      // Step 7 mutex: see toggleMeasure for rationale. If we're about to
      // ENTER walk, exit measure first.
      if (!internal.walkState.active && internal.measureState.active) {
        dispatchMeasurementEvent({ type: 'EXIT' });
      }
      if (!internal.walkState.active) {
        exitClickToMoveIfActive();
        cancelHotspotPlacementIfActive(); // F8 reverse mutex
      }
      dispatchWalkEvent({ type: 'TOGGLE_ACTIVE' });
    },
    exitWalk(): void {
      dispatchWalkEvent({ type: 'EXIT' });
    },
    getWalkSnapshot(): WalkSnapshot {
      return walkSnapshot(internal.walkState);
    },
    getWalkInputMode(): 'keyboard' | 'touch' | null {
      return internal.walkInputMode;
    },
    getViewboxSnapshot(): ViewboxSnapshot {
      return viewboxSnapshot(internal.viewboxState);
    },
    getRideSnapshot(): RideSnapshot {
      return rideSnapshot(internal.rideState, performance.now());
    },
    async selectViewbox(arrayIdx: number): Promise<void> {
      // Pre-validate idx: il reducer (PR3.1) fa il check rigoroso (integer +
      // bounds + non mid-glide + non same), no-op se invalido → niente
      // flyToCamera spawned su event ignorato.
      const prev = internal.viewboxState;
      const target = prev.available[arrayIdx];
      if (
        target === undefined ||
        prev.teleportPhase !== 'idle' ||
        arrayIdx === prev.currentArrayIdx
      ) {
        return;
      }
      // WS5 PR5: ride attiva → no teleport esplicito (mutex camera ownership).
      // Il ride si concludera' con dispatchViewboxEvent('SELECT') che gia'
      // aggiorna currentArrayIdx senza altro glide.
      if (internal.rideState.phase !== 'idle') return;
      // WS5 PR3.3c mutex: durante il cinematic tour la camera e' di
      // proprieta' del sequencer → niente teleport viewbox-to-viewbox
      // interleaved (creerebbe race con flyToCamera concorrenti).
      if (internal.cinematicActive) return;
      // WS5 PR3.2b (Codex W1): mutex XR. selectViewbox NON deve agire mentre
      // XR sta avviando o e' in sessione: la camera e' di proprieta' del
      // session XR. Pattern allineato a showOverview (no-op su XR active).
      if (internal.xrState.status === 'starting' || internal.xrState.status === 'in-session') {
        return;
      }
      // Mutex con altri controlli camera (allineato pattern applyPreset).
      if (internal.hotspotsState.openIndex !== -1) {
        dispatchHotspotEvent({ type: 'CLOSE' });
      }
      if (internal.tourState.kind !== 'idle') {
        dispatchTourEvent({ type: 'EXIT' });
      }
      // Compute camera target dal default_camera del viewbox (fov rad→deg).
      const cam = target.default_camera;
      const yaw = cam.yaw;
      const pitch = cam.pitch;
      const cosP = Math.cos(pitch);
      const fwdX = -Math.sin(yaw) * cosP;
      const fwdY = Math.sin(pitch);
      const fwdZ = -Math.cos(yaw) * cosP;
      const fovDeg = (cam.fov * 180) / Math.PI;
      const to: CameraTarget = {
        pos: [cam.pos[0], cam.pos[1], cam.pos[2]],
        lookAt: [cam.pos[0] + fwdX, cam.pos[1] + fwdY, cam.pos[2] + fwdZ],
        fov: fovDeg,
      };
      // Dispatch TELEPORT_START: phase='gliding' → walkTick si sospende +
      // walkState slice idx aggiornato (per AABB clamp al resume).
      dispatchViewboxEvent({ type: 'TELEPORT_START', arrayIdx });
      // WS5 PR3.2b (Codex W2): reset walkVY se walk e' active. Senza reset,
      // un salto/caduta in corso continuerebbe sotto la nuova viewbox al
      // resume del walkTick post-TELEPORT_COMPLETE.
      if (internal.walkState.active) {
        internal.walkVY = 0;
        internal.walkJumpHeld = false;
      }
      // WS5 PR3.2b (Codex W3): durata glide custom (matterport-style ~800ms
      // desktop / 600ms mobile) invece di TRANSITION_MS=4000 (legato preset
      // tour). reduced-motion → 0ms (skip animazione, set diretto).
      const durationMs = deps.deviceMatcher.snapshot.reducedMotion
        ? 0
        : deps.deviceMatcher.snapshot.pointerCoarse
          ? TELEPORT_GLIDE_MS_MOBILE
          : TELEPORT_GLIDE_MS;
      // Glide: replica del pattern flyManualTo ma con durationMs custom +
      // dispatch TELEPORT_COMPLETE/ABORT su success/abort.
      const cur = currentCamera();
      if (internal.controls !== null) internal.controls.enabled = false;
      internal.tourAbort?.abort();
      internal.clickToMoveAbort?.abort();
      const localCtrl = new AbortController();
      internal.tourAbort = localCtrl;
      try {
        await flyToCamera(deps.adapter, internal.controls, cur, to, {
          durationMs,
          signal: localCtrl.signal,
        });
        if (internal.tourAbort === localCtrl) {
          // Sub-task 1.2 (2026-06-05): walk-aware teleport sync. Quando
          // walk e' active, allinea yaw/pitch al target.default_camera +
          // resetta input buffers (keys/touch/jump/vy) PRIMA di emettere
          // TELEPORT_COMPLETE. Senza questo blocco, walkTick post-glide
          // usa il forwardVector con yaw/pitch stale e l'utente "guarda
          // altrove" non appena il tick riprende. Reset input previene
          // anche keys held + joystick "premuto" che continuano il
          // movimento sulla nuova viewbox subito al resume.
          if (internal.walkState.active) {
            internal.walkYaw = yaw;
            internal.walkPitch = clampPitch(pitch);
            internal.walkVY = 0;
            internal.walkJumpHeld = false;
            internal.walkKeysHeld.clear();
            internal.walkTouchInput = null;
            internal.walkLastTickMs = performance.now();
          }
          dispatchViewboxEvent({ type: 'TELEPORT_COMPLETE' });
        }
      } catch (err) {
        if (!isAbortError(err)) console.warn('[orchestrator] selectViewbox failed:', err);
      } finally {
        // Codex C1 fix: ora flyToCamera reject su throw RAF (try/catch
        // dentro tick), quindi il finally arriva sempre. Cleanup phase
        // se ancora gliding (abort esterno, throw, ecc.).
        if (internal.viewboxState.teleportPhase === 'gliding') {
          dispatchViewboxEvent({ type: 'TELEPORT_ABORT' });
        }
        // XR-active check rimossa dal finally (Codex iter2 nota): una
        // transition XR DURING await glide e' teoricamente possibile (dispatch
        // sincrono via tasto rapido o handler programmatico), ma e' gia'
        // coperta dal token-gate `internal.tourAbort === localCtrl`. Quando XR
        // attiva via enterXR(), abortManualCameraFly() chiama
        // tourAbort?.abort() → tourAbort viene riassegnato → il nostro check
        // fallisce → controls NON vengono riabilitati e XR mantiene
        // ownership. Quindi il finally e' safe anche senza guard XR esplicita.
        if (
          internal.tourAbort === localCtrl &&
          internal.tourState.kind === 'idle' &&
          !internal.walkState.active &&
          internal.controls !== null
        ) {
          internal.controls.enabled = true;
        }
      }
    },
    // WS5 PR3.3c: cinematic auto-tour. Idle → start; Active → abort.
    // Guard mutex: no-op se viewboxes<2, walkActive, XR starting/in-session,
    // tour preset in transition, scena non caricata. Vedi PLAN §11.
    toggleCinematicTour(): void {
      if (internal.disposed) return;
      if (internal.cinematicActive) {
        // User skip → abort sia loop sia landing in volo (Codex iter2 W3b).
        abortCinematicAll();
        return;
      }
      const N = internal.viewboxState.available.length;
      if (N < 2) return;
      if (internal.walkState.active) return;
      if (internal.xrState.status === 'starting' || internal.xrState.status === 'in-session') {
        return;
      }
      if (internal.tourState.kind !== 'idle') return;
      // WS5 PR5: ride attiva → cinematic prende il posto (abort ride). Caller
      // ha gia' deciso start cinematic, quindi prevale.
      if (internal.rideState.phase !== 'idle') {
        dispatchRideEvent({ type: 'ABORT', nowMs: performance.now() });
      }
      void runCinematicTourInOrchestrator();
    },
    getCinematicActive(): boolean {
      return internal.cinematicActive;
    },
    toggleXR(): void {
      const s = internal.xrState;
      // 'starting' is intentionally a no-op (orphan session prevention).
      if (s.status === 'starting') return;
      if (s.status === 'in-session') {
        void api.exitXR();
        return;
      }
      if (s.modalOpen) {
        api.closeXRModal();
        return;
      }
      api.openXRModal();
    },
    openXRModal(): void {
      dispatchXREvent({ type: 'OPEN_MODAL' });
    },
    closeXRModal(): void {
      dispatchXREvent({ type: 'CLOSE_MODAL' });
    },
    enterXR(mode: XRMode): void {
      cancelHotspotPlacementIfActive(); // F8 reverse mutex (XR prende la camera)
      dispatchXREvent({ type: 'SESSION_REQUEST', mode });
    },
    async exitXR(): Promise<void> {
      if (!canExitXR(deps.adapter)) return;
      // Codex iter 4 plan MAJOR #1 fix: bump xrSessionSeq when status
      // is 'starting' so the still-pending adapter.enterXR.catch
      // (captured an older seq) is invalidated and won't dispatch
      // ERROR after we tear down.
      const wasStarting = internal.xrState.status === 'starting';
      if (wasStarting) internal.xrSessionSeq += 1;
      try {
        await deps.adapter.exitXR();
      } catch (err) {
        console.warn('[orchestrator] exitXR threw:', err);
      }
      // For the 'starting' path the adapter cancelled the pending
      // session WITHOUT firing the 'xr-session-ended' event (the
      // session never reached setSession). Dispatch SESSION_ENDED
      // locally so the SM transitions starting → idle.
      if (wasStarting && !internal.disposed) {
        dispatchXREvent({ type: 'SESSION_ENDED' });
      }
    },
    getXRSnapshot(): XRSnapshot {
      return xrSnapshot(internal.xrState);
    },
    getDeviceCapabilities(): DeviceCapabilities {
      return deps.deviceMatcher.snapshot;
    },
    notifyUnavailableFeature(reason: 'walk-touch'): void {
      // WS5 PR4a — Mobile FP attivo by default. Quando il flag mobile-fp
      // e' OFF (rollback kill-switch) torniamo al vecchio toast educational.
      // Quando ON il callback diventa no-op (il toolbar non lo chiama piu'
      // perche' setWalkDisabled non e' triggerato da pointerCoarse).
      if (reason === 'walk-touch' && !isMobileFpEnabled()) {
        emit('walk-error', { reason: 'touch', embed: deps.embed });
      }
    },
    getCameraState() {
      // 5.C iter 4: refuse to return a fallback lookAt [0,0,0] when
      // controls are missing. Without controls.target the orchestrator
      // cannot know what the user is aiming at, and authoring would
      // silently save hotspot/preset with a wrong target. The Studio
      // bridge gates this call by waiting for `viewer-ready`, which
      // is only emitted when controls attached successfully.
      if (internal.controls === null) {
        throw new Error('getCameraState: orbit controls not attached');
      }
      const cur = currentCamera();
      const fov = Math.min(170, Math.max(10, Number.isFinite(cur.fov) ? cur.fov : 50));
      const finite3 = (v: readonly number[]): readonly [number, number, number] => {
        const x = Number.isFinite(v[0]) ? (v[0] as number) : 0;
        const y = Number.isFinite(v[1]) ? (v[1] as number) : 0;
        const z = Number.isFinite(v[2]) ? (v[2] as number) : 0;
        return [x, y, z] as const;
      };
      return {
        cam_pos: finite3(cur.pos),
        cam_lookat: finite3(cur.lookAt),
        cam_fov: fov,
      };
    },
    raycastFromScreen(screenX: number, screenY: number) {
      if (!canRaycast(deps.adapter)) return null;
      let hit: Vec3 | null;
      try {
        hit = raycastDispatch(deps.adapter, screenX, screenY);
      } catch {
        return null;
      }
      if (hit === null) return null;
      const x = Number.isFinite(hit[0]) ? hit[0] : null;
      const y = Number.isFinite(hit[1]) ? hit[1] : null;
      const z = Number.isFinite(hit[2]) ? hit[2] : null;
      if (x === null || y === null || z === null) return null;
      return [x, y, z] as const;
    },
    raycastCenter() {
      if (!canRaycast(deps.adapter)) return null;
      const renderer = deps.adapter.getRenderer();
      const w = renderer.domElement.clientWidth;
      const h = renderer.domElement.clientHeight;
      if (w <= 0 || h <= 0) return null;
      const hit = raycastDispatch(deps.adapter, w / 2, h / 2);
      if (hit === null) return null;
      const x = Number.isFinite(hit[0]) ? hit[0] : null;
      const y = Number.isFinite(hit[1]) ? hit[1] : null;
      const z = Number.isFinite(hit[2]) ? hit[2] : null;
      if (x === null || y === null || z === null) return null;
      return [x, y, z] as const;
    },
    replaceHotspots(hotspots) {
      // Capture the active hotspot ID BEFORE swapping the sorted list,
      // so we can detect removal even when another entry takes the
      // same index after sort.
      const cur = internal.hotspotsState;
      const activeId =
        cur.openIndex >= 0 ? (internal.sortedHotspots[cur.openIndex]?.id ?? null) : null;
      // F11a: firma del CONTENUTO renderizzato (id/ordine/titolo/descrizione/media)
      // PRIMA dello swap. strip/dots/popup fissano testo+id+gallery al mount e il
      // loro update() NON li riscrive → vanno re-montati a OGNI cambio contenuto,
      // non solo quando la cardinalità attraversa 0↔N (bug F11a).
      const prevSig = hotspotsRenderSignature(internal.sortedHotspots);
      const sorted = sortHotspots(hotspots);
      const newActiveIndex = activeId !== null ? sorted.findIndex((h) => h.id === activeId) : -1;
      // F11 (LOW, iter2): hotspot attivo RIMOSSO → CLOSE PRIMA dello swap, così la
      // transizione gira anche se la nuova lista è vuota (la SM no-op-pa con
      // hotspotsLen===0) e i side effect di close (abort fly / re-enable controls)
      // vengono eseguiti invece di lasciare un openIndex stantio.
      if (activeId !== null && newActiveIndex < 0) {
        dispatchHotspotEvent({ type: 'CLOSE' });
      }
      internal.sortedHotspots = sorted;
      // F11 (LOW): hotspot attivo sopravvissuto a un NUOVO indice (delete-before /
      // reorder) → rimappa openIndex per non lasciare popup/dot sull'indice
      // sbagliato. Mutazione diretta (NO camera-fly: OPEN rieseguirebbe i fly).
      if (newActiveIndex >= 0 && newActiveIndex !== cur.openIndex) {
        internal.hotspotsState = { openIndex: newActiveIndex, visible: cur.visible };
      }
      emit('hotspot-state', hotspotsSnapshot(internal.hotspotsState, internal.sortedHotspots));
      // Re-mount the Loaded hotspot UI on any content change (title/description/
      // media/order/count) so strip/dots/popup show fresh data. Was 5.C iter 6
      // limited to 0↔N (mountHotspotDots is no-op when wired with an empty list).
      if (prevSig !== hotspotsRenderSignature(sorted)) {
        emit('metadata-replaced', { kind: 'hotspots' });
      }
    },
    replacePresets(presets) {
      const ts = internal.tourState;
      const activeIndex =
        ts.kind === 'transitioning'
          ? ts.to
          : ts.kind === 'dwelling' || ts.kind === 'paused'
            ? ts.index
            : -1;
      const activeId = activeIndex >= 0 ? (internal.sortedPresets[activeIndex]?.id ?? null) : null;
      const oldEmpty = internal.sortedPresets.length === 0;
      const sorted = sortPresets(presets);
      internal.sortedPresets = sorted;
      // Exit the tour if the active preset disappeared (compare by id,
      // not just by length — a different preset might now occupy the
      // same index after sort).
      if (ts.kind !== 'idle' && activeId !== null && !sorted.some((p) => p.id === activeId)) {
        dispatchTourEvent({ type: 'EXIT' });
      }
      emit(
        'tour-state',
        tourSnapshot(internal.tourState, internal.sortedPresets, performance.now()),
      );
      const newEmpty = sorted.length === 0;
      if (oldEmpty !== newEmpty) emit('metadata-replaced', { kind: 'presets' });
    },
    getFloorplan(): FloorPlan | null {
      return internal.floorplan;
    },
    getFloorplanSnapshot(): FloorplanSnapshot {
      return floorplanSnapshot(internal.floorplanState);
    },
    replaceFloorplan(fp: FloorPlan | null) {
      const had = internal.floorplan !== null;
      const has = fp !== null;
      internal.floorplan = fp;
      internal.floorplanState = {
        visible: has,
        imageError: false,
        degenerateCalibration: has && isCalibrationDegenerate(fp.calibration),
      };
      // Reset the throttle clock so the renderer's first remount sees a
      // fresh emit on the next ticker frame (the per-frame
      // 'floorplan-position' loop guards on internal.floorplan === null).
      internal.lastFloorplanEmit = 0;
      emit('floorplan-state', floorplanSnapshot(internal.floorplanState));
      if (had !== has || has) {
        // 5.D iter 2 add: emit on every replace (not only 0<->1) so the
        // renderer cleans up the minimap when the image_url changes
        // (replace existing). Renderer resets currentFloorplanPosition
        // before remount.
        emit('floorplan-replaced', { hadFloorplan: had, hasFloorplan: has });
      }
    },
    async shareUrl(): Promise<void> {
      let cam: import('../viewer/adapter.js').CameraFit;
      try {
        const camera = deps.adapter.getCamera();
        const target = internal.controls?.target;
        cam = {
          pos: [camera.position.x, camera.position.y, camera.position.z],
          lookAt: target ? [target.x, target.y, target.z] : [0, 0, 0],
          fov: camera.fov,
        };
      } catch (err) {
        emit('share-result', {
          success: false,
          message: err instanceof Error ? err.message : 'camera non disponibile',
        });
        return;
      }
      const encoded = encodeCameraState(cam);
      const shareHref = buildShareUrl(window.location.href, encoded);
      try {
        await copyText(shareHref);
        // replaceState success-only: refresh continues from this view; does
        // not pollute history with intermediate share clicks.
        history.replaceState(null, '', shareHref);
        emit('share-result', { success: true });
      } catch (err) {
        emit('share-result', {
          success: false,
          message: err instanceof Error ? err.message : 'clipboard non disponibile',
        });
      }
    },
  };

  const onStateChange = (state: AppState): void => {
    if (internal.disposed) return;
    switch (state.kind) {
      case 'fetching-meta':
        void fetchMeta(state.route);
        break;
      case 'loading-scene':
        // Only kick off scene load on entry to this state. State updates from
        // MODEL_PROGRESS / METADATA_PARTIAL keep state.kind === 'loading-scene'
        // but the loadScene promise is already running; do nothing.
        break;
      default:
        break;
    }
  };

  let lastKindStarted: AppState['kind'] | null = null;
  const wrappedListener = (state: AppState): void => {
    if (state.kind !== lastKindStarted) {
      lastKindStarted = state.kind;
      onStateChange(state);
      if (state.kind === 'loading-scene') {
        void loadScene(state.route, state.meta);
      }
    }
  };

  api.start = async (): Promise<void> => {
    if (internal.disposed) return;
    internal.unsubscribe = deps.store.subscribe(wrappedListener);

    // 4.M: subscribe to device matcher and re-broadcast as
    // 'device-state' event. The renderer listens via the same
    // EventTarget channel as walk-state / xr-state etc., so the UI
    // pattern stays consistent. We also emit the initial snapshot
    // immediately so the renderer's first read is consistent with
    // the matcher's current state.
    internal.unsubDeviceMatcher = deps.deviceMatcher.subscribe((next) => {
      if (internal.disposed) return;
      emit('device-state', next);
      // Inc D: pointerCoarse flip changes post-FX availability (mobile MVP →
      // unavailable). Re-derive the effective state + re-broadcast.
      applyPostFxState();
    });
    emit('device-state', deps.deviceMatcher.snapshot);

    const initDeviceClass = detectDeviceClass(readDeviceClassInputs(deps.deviceMatcher.snapshot));
    const maxPixelRatio = initDeviceClass === 'mobile' ? MAX_DPR_MOBILE : MAX_DPR_DESKTOP;
    const lodSplatCount =
      initDeviceClass === 'mobile' ? LOD_SPLAT_COUNT_MOBILE : LOD_SPLAT_COUNT_DESKTOP;
    const initOpts = buildInitOptions(deps.canvas, deps.embed, maxPixelRatio, lodSplatCount);
    try {
      await deps.adapter.init(initOpts);
    } catch (err) {
      console.error('[orchestrator] adapter init failed:', err);
      deps.store.dispatch({
        type: 'ERROR_TRANSIENT',
        route: null,
        reason: 'adapter-init-failed',
        canRetry: false,
        attempt: 1,
      });
      // 5.C: also surface to embedded bridge so the editor SM can reach
      // model-error instead of staying in loading-model forever.
      const message = err instanceof Error ? err.message : 'adapter-init-failed';
      emit('viewer-failure', { message });
      return;
    }

    deps.store.dispatch({ type: 'PARSE_URL', href: deps.href });
    const parsed = parseShareUrl(deps.href);
    deps.store.dispatch({ type: 'URL_PARSED', result: parsed });
  };

  api.dispose = async (): Promise<void> => {
    if (internal.disposed) return;
    internal.disposed = true;
    internal.abortController.abort();
    cancelHotspotPlacementIfActive(); // F8: detach armed placement listeners
    // WS5 PR5: dispose textures shared del modulo ride (pin + halo). NON
    // strict necessario se loadedTeardown ha gia' rimosso la layer, ma le
    // textures sono cached module-scoped → dispose qui evita leak se viewer
    // viene ri-istanziato (test/multi-view).
    disposeRidePinTextures();
    teardownLoadedFeatures();
    if (internal.unsubscribe !== null) {
      internal.unsubscribe();
      internal.unsubscribe = null;
    }
    // 4.M: stop re-broadcasting matcher changes. The matcher itself
    // is owned by bootstrap (main.ts/embed.ts) which calls dispose()
    // AFTER this — we just unsubscribe so no more 'device-state'
    // events fire post-dispose.
    if (internal.unsubDeviceMatcher !== null) {
      internal.unsubDeviceMatcher();
      internal.unsubDeviceMatcher = null;
    }
    if (canSetBeforeRender(deps.adapter)) {
      deps.adapter.setBeforeRender(null);
    }
    if (internal.controls !== null) {
      internal.controls.dispose();
      internal.controls = null;
    }
    revokeBlob();
    if (canDispose(deps.adapter)) {
      await deps.adapter.dispose();
    }
  };

  return api;
}

// F11a: firma dei campi hotspot che la UI viewer fissa al mount (strip chip
// "n. titolo", dot label/id, popup titolo/descrizione/gallery). pos/cam esclusi:
// la posizione del dot è proiettata live e cam serve solo al fly-to. Separatori
// con control-char per evitare collisioni tra campi/elementi.
function hotspotsRenderSignature(hotspots: readonly Hotspot[]): string {
  return hotspots
    .map(
      (h) =>
        `${h.id}${h.order_index}${h.title}${h.description ?? ''}${(h.media ?? [])
          .map((m) => `${m.id}:${m.caption ?? ''}`)
          .join(',')}`,
    )
    .join('');
}

function ensureMetadataPresent(store: AppStore, metadata: SceneMetadata): void {
  // The orchestrator may have dispatched METADATA_PARTIAL only for successful
  // endpoints; failures use defaults from fetchMetadataTolerant. We dispatch
  // any missing keys here so the SCENE_LOADED transition (which requires
  // complete metadata) succeeds.
  const current = store.getState();
  if (current.kind !== 'loading-scene') return;
  const partial = current.metadata;
  if (!('hotspots' in partial)) {
    store.dispatch({ type: 'METADATA_PARTIAL', key: 'hotspots', value: metadata.hotspots });
  }
  if (!('presets' in partial)) {
    store.dispatch({ type: 'METADATA_PARTIAL', key: 'presets', value: metadata.presets });
  }
  if (!('floorplan' in partial)) {
    store.dispatch({ type: 'METADATA_PARTIAL', key: 'floorplan', value: metadata.floorplan });
  }
}
